import { AsyncPipe, NgClass } from '@angular/common';
import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    GroupRideDeviceProfileContract,
    MapItemType,
    type MapboxMap,
    RideType,
    UserStoreService,
    UtilService,
    VehicleType,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    Vehicle,
    VehicleLocation,
} from '@polaris-customerweb/sharedcomponents.garage';
import {
    Dealer,
    MapboxService,
    RideArea,
    TrailMapService,
} from '@polaris-customerweb/sharedcomponents.map';
import { SearchResult } from '@polaris-customerweb/sharedcomponents.search';
import {
    ConfirmDialogComponent,
    ConfirmDialogOptions,
    ConsoleLogService,
    GeoBounds,
    GeoCoordinate,
    ImageCdnSrcDirective,
    IsMobileViewService,
    LogService,
    MapPolygon,
    MapPolyline,
    UserLocationService,
} from '@polaris-customerweb/sharedcomponents.shared';
import {
    Geofence,
    PlannedRide,
    ShowOnMapService,
    TrackedRide,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import {
    UserProfile,
    UserProfileService,
} from '@polaris-customerweb/sharedcomponents.user-profile';
import {
    ActiveVehicleAlert,
    ConnectedVehicleService,
    LocationAlertPath,
    RemoteOperationsService,
    VehiclePermissionService,
} from '@polaris-customerweb/sharedcomponents.vehicle-health';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { attempt, compact, debounce, defer, includes, some } from 'lodash-es';
import type { MapMouseEvent, MapOptions, ScaleControl } from 'mapbox-gl';
import { NEVER, Observable, combineLatest, forkJoin, merge, of } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    exhaustMap,
    filter,
    map,
    pairwise,
    shareReplay,
    skip,
    startWith,
    switchMap,
    take,
    takeWhile,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { selectActiveAdventure } from 'src/app/components/adventure/adventure.reducer';
import { selectActiveCollection } from 'src/app/components/common/collection/collection.reducer';
import { selectActiveRideEvent } from 'src/app/components/common/ride-event/ride-event.reducer';
import { selectActiveDealer } from 'src/app/components/dealer/dealer.reducer';
import {
    selectActiveGeofence,
    selectEditableGeofence,
    selectIsGeofenceEditing,
} from 'src/app/components/geofence/geofence.reducer';
import { selectActiveParcel } from 'src/app/components/parcels/parcels.reducer';
import {
    selectActivePlannedRide,
    selectAdhocWaypoint,
    selectEditablePlannedRide,
    selectIsRidePlanning,
    selectTempViapoint,
} from 'src/app/components/planned-ride/planned-ride.reducer';
import { SetPlacesInView } from 'src/app/components/point-of-interest/point-of-interest.actions';
import {
    selectActivePointOfInterest,
    selectActivePremiumPoi,
    selectPremiumPointsOfInterestInView,
    selectStandardPlacesInView,
} from 'src/app/components/point-of-interest/point-of-interest.reducer';
import { selectActiveRideArea } from 'src/app/components/ride-area/ride-area.reducer';
import { UpdateRideConditionsInView } from 'src/app/components/ride-condition/ride-condition.actions';
import {
    selectActiveRideCondition,
    selectRideConditionsInView,
} from 'src/app/components/ride-condition/ride-condition.reducer';
import {
    SetActiveRideGroupLeader,
    StartFollowingRideGroup,
    StopFollowingLeader,
} from 'src/app/components/ride-group/ride-group.actions';
import {
    selectActiveRideGroup,
    selectActiveRideGroupRider,
    selectFollowingState,
    selectRideGroupRiders,
} from 'src/app/components/ride-group/ride-group.reducer';
import {
    selectActiveTrackedRide,
    selectIsTrackedRideEditing,
} from 'src/app/components/tracked-ride/tracked-ride.reducer';
import { SetPublicWaypointsInView } from 'src/app/components/waypoint/waypoint.actions';
import {
    selectActivePublicWaypoint,
    selectActiveWaypoint,
    selectEditableWaypoint,
    selectIsWaypointEditing,
    selectPublicWaypointsInView,
} from 'src/app/components/waypoint/waypoint.reducer';
import { AnalyticsDataDirective } from 'src/app/directives/analytics-data.directive';
import { AnalyticsDirective } from 'src/app/directives/analytics.directive';
import { AppState } from 'src/app/models/app-state';
import { Collection } from 'src/app/models/collection';
import { MapDisplay } from 'src/app/models/map-display';
import { MapPoint } from 'src/app/models/map-point';
import { Parcel } from 'src/app/models/parcel';
import { RideEvent } from 'src/app/models/ride-event';
import { RideGroup } from 'src/app/models/ride-group';
import { RideGroupRiderStatus } from 'src/app/models/ride-group-rider-status';
import { AdventureMapService } from 'src/app/services/adventure-map.service';
import { CheckInContactMessageMapService } from 'src/app/services/check-in-contact-message-map.service';
import { DealerMapService } from 'src/app/services/dealer-map.service';
import { DealerService } from 'src/app/services/dealer.service';
import { GeofenceMapService } from 'src/app/services/geofence-map.service';
import { HeatmapService } from 'src/app/services/heatmap.service';
import { InProgressRideMapService } from 'src/app/services/in-progress-ride-map.service';
import { MapService } from 'src/app/services/map.service';
import { ParcelsInformationService } from 'src/app/services/parcels-information.service';
import { PlannedRideMapService } from 'src/app/services/planned-ride-map.service';
import { PlannedRideService } from 'src/app/services/planned-ride.service';
import { PointOfInterestMapService } from 'src/app/services/point-of-interest-map.service';
import { PremiumPoiMapService } from 'src/app/services/premium-poi-map.service';
import { PublicWaypointMapService } from 'src/app/services/public-waypoint-map.service';
import { RideAreaMapService } from 'src/app/services/ride-area-map.service';
import { RideAreaService } from 'src/app/services/ride-area.service';
import { RideConditionMapService } from 'src/app/services/ride-condition-map.service';
import { RideGroupMapService } from 'src/app/services/ride-group-map.service';
import { RideTypeService } from 'src/app/services/ride-type.service';
import { TerrainMapService } from 'src/app/services/terrain-map.service';
import { TrackedRideMapService } from 'src/app/services/tracked-ride-map.service';
import { WaypointMapService } from 'src/app/services/waypoint-map.service';
import { WeatherService } from 'src/app/services/weather.service';
import { AnalyticsService } from 'src/app/state/analytics/analytics.service';
import { WINDOW } from 'src/app/state/app-config/factories/window.factory';
import { ContactMessageVm } from 'src/app/state/check-in-contacts/contact-message.vm';
import { selectEditableSponsor } from 'src/app/state/club/club.reducer';
import { InProgressRideVm } from 'src/app/state/in-progress-ride/in-progress-ride.vm';
import {
    GetLayerVisibility,
    SetIsShowingAdventures,
    SetIsShowingDealers,
    SetIsShowingGeofences,
    SetIsShowingPlannedRides,
    SetIsShowingPublicWaypoints,
    SetIsShowingRideAreas,
    SetIsShowingTrackedRides,
    SetIsShowingWaypoints,
} from 'src/app/state/map/layer-visibility/layer-visibility.actions';
import {
    selectIsShowingAdventures,
    selectIsShowingCloudCover,
    selectIsShowingDealers,
    selectIsShowingDesignatedPavedTrails,
    selectIsShowingDesignatedUnpavedTrails,
    selectIsShowingFood,
    selectIsShowingFuel,
    selectIsShowingGeofences,
    selectIsShowingHeatmap,
    selectIsShowingInProgressRide,
    selectIsShowingLand,
    selectIsShowingLodging,
    selectIsShowingPlannedRides,
    selectIsShowingRadar,
    selectIsShowingRideAreas,
    selectIsShowingRideConditions,
    selectIsShowingSatellite,
    selectIsShowingSnowDepth,
    selectIsShowingTrackedRides,
    selectIsShowingTrailDifficulty,
    selectIsShowingTrails,
    selectIsShowingUnknown,
    selectIsShowingUnmaintainedTrails,
    selectIsShowingWaypoints,
    selectMaxRideConditionsAgeInHours,
    selectTrailFilter,
} from 'src/app/state/map/layer-visibility/layer-visibility.reducer';
import { MapTestService } from 'src/app/state/map/map-test.service';
import { MapExtents } from 'src/app/state/map/map.reducer';
import {
    selectSearchIsActive,
    selectSearchResults,
} from 'src/app/state/search/search.selectors';
import { UserAuthService } from 'src/app/state/user-auth/user-auth.service';
import { selectActiveVehicleLocation } from 'src/app/state/vehicle/vehicle-location/vehicle-location.selectors';
import { MapDrawerComponent } from '../map-drawer/map-drawer.component';
import { TrailLegendButtonComponent } from '../map-legend/trail-legend-button/trail-legend-button.component';
import { MapDebugComponent } from './map-debug/map-debug.component';
import { SearchThisAreaButtonComponent } from './search-this-area-button/search-this-area-button.component';

@Component({
    imports: [
        AnalyticsDataDirective,
        AnalyticsDirective,
        AngularSvgIconModule,
        AsyncPipe,
        ImageCdnSrcDirective,
        MapDebugComponent,
        MapDrawerComponent,
        NgClass,
        RouterLink,
        SearchThisAreaButtonComponent,
        TrailLegendButtonComponent,
    ],
    selector: 'rdx-map',
    standalone: true,
    styleUrls: ['./map.component.scss'],
    templateUrl: './map.component.html',
})
export class MapComponent implements OnInit, OnDestroy {
    @Input() activeAlerts$: Observable<ActiveVehicleAlert[]>;
    @Input() deviceProfiles$: Observable<GroupRideDeviceProfileContract[]>;
    @Input() initialBounds?: Observable<GeoBounds | null>;
    @Input() initialMapCenter?: GeoCoordinate;
    @Input() initialMapZoom?: number;
    @Input() initialRideType: RideType;
    @Input() initializeInSatellite: boolean;
    @Input() showLabelledPois: boolean;
    @Input() enableAddWaypoint: boolean;
    @Input() enableMapTypeToggle?: boolean;
    @Input() enableMapControls = true;
    @Input() enableMapLegend?: boolean;
    @Input() enableLargerMapBtn = false;
    @Input() enableCloudCover = true;
    @Input() enableRadarWeather = true;
    @Input() enableRideCondition = true;
    @Input() enableSnowDepth = true;
    @Input() enableLandInfo = false;
    @Input() enableZoomControl = false;
    @Input() viewOnMapUrl: string;
    @Input() enablePlannedRideIntro = false;
    @Input() disableDragPan = false;
    @Input() forcePoisOn = false;
    @Input() hideMapScale = false;
    @Input() contactMessage$: Observable<ContactMessageVm | null>;
    @Input() activeInProgressRideId$: Observable<string | null>;
    @Input() activeVehicleId$: Observable<string>;
    @Input() geofences$?: Observable<readonly Geofence[]>;
    @Input() inProgressRides$: Observable<readonly InProgressRideVm[]>;
    @Input() locationAlertPaths$: Observable<LocationAlertPath[]>;
    @Input() plannedRides$?: Observable<readonly PlannedRide[]>;
    @Input() trackedRides$?: Observable<readonly TrackedRide[]>;
    @Input() waypoints$?: Observable<readonly Waypoint[]>;
    @Input() vehicles$: Observable<readonly Vehicle[]>;
    @Input() vehiclesLocations$: Observable<readonly VehicleLocation[]> = of(
        []
    );
    @Input() containerId = 'map';

    @Output() createWaypoint = new EventEmitter<void>();
    @Output() mapInitialized = new EventEmitter<MapDisplay>();

    readonly className = 'MapComponent';
    areGeofencesEnabled: boolean;
    arePublicWaypointsEnabled: boolean;
    areRideAreasEnabled: boolean;
    areSponsorsEnabled: boolean; // todo: rename to premiumPlaces
    isRadarVisible = false;
    isCloudCoverVisible = false;
    isSnowDepthVisible = false;
    isRideConditionVisible = false;
    isSatellite$: Observable<boolean>;
    isDebug: boolean;
    mapDisplay: MapDisplay;
    rideType$: Observable<VehicleType>;
    $isMobileView = this.isMobileViewService.$isMobileView;
    vehicleType$: Observable<string>;
    hasLocationPermission$: Observable<boolean>;
    hasViewCurrentRidePermission$: Observable<boolean>;
    activeRideGroup$: Observable<RideGroup | undefined>;
    vehicleIsSelected: boolean;
    hasBrowserLocationPermission: boolean;
    // debug
    layers: string[];
    mouseLng: number;
    mouseLat: number;
    north: number;
    east: number;
    south: number;
    west: number;
    zoom: number;
    isLeft: boolean;

    private destructors: (() => void)[] = [];
    private isDebuggingLayers = false;
    private isDestroyed = false;
    private layerIdsSelectable: string[] = [];
    private layerIdToItemTypeMap: Record<string, MapItemType>;
    private legendExpanded = false;
    private maxInitialZoom = 15;

    constructor(
        private activatedRoute: ActivatedRoute,
        private analyticsService: AnalyticsService,
        private configService: ConfigService,
        private connectedVehicleService: ConnectedVehicleService,
        private consoleLogService: ConsoleLogService,
        private checkInContactMessageMapService: CheckInContactMessageMapService,
        private dealerMapService: DealerMapService,
        private adventureMapService: AdventureMapService,
        private dealerService: DealerService,
        private geofenceMapService: GeofenceMapService,
        private userLocationService: UserLocationService,
        private heatmapService: HeatmapService,
        private inProgressRideMapService: InProgressRideMapService,
        private logService: LogService,
        private mapboxgl: MapboxService,
        private mapService: MapService,
        private matDialog: MatDialog,
        private matSnackBar: MatSnackBar,
        private parcelsInformationService: ParcelsInformationService,
        private plannedRideMapService: PlannedRideMapService,
        private plannedRideService: PlannedRideService,
        private pointOfInterestMapService: PointOfInterestMapService,
        private premiumPoiMapService: PremiumPoiMapService,
        private publicWaypointMapService: PublicWaypointMapService,
        private remoteOperationsService: RemoteOperationsService,
        private rideAreaMapService: RideAreaMapService,
        private rideAreaService: RideAreaService,
        private rideConditionMapService: RideConditionMapService,
        private rideGroupMapService: RideGroupMapService,
        private rideTypeService: RideTypeService,
        private router: Router,
        private showOnMapService: ShowOnMapService,
        private store: Store<AppState>,
        private terrainMapService: TerrainMapService,
        private trackedRideMapService: TrackedRideMapService,
        private trailMapService: TrailMapService,
        private userAuthService: UserAuthService,
        private userProfileService: UserProfileService,
        private userStoreService: UserStoreService,
        private utilService: UtilService,
        private vehiclePermissionService: VehiclePermissionService,
        private waypointMapService: WaypointMapService,
        private weatherService: WeatherService,
        private isMobileViewService: IsMobileViewService,
        @Inject(WINDOW) private window: Window
    ) {
        // TODO: this should get moved to the parent component
        if (this.router.url === '/map') {
            const lastPlannedRideUrl =
                this.plannedRideService.getLastPlannedRideUrl();
            if (lastPlannedRideUrl) {
                void this.router.navigateByUrl(lastPlannedRideUrl);
            }
        }

        this.store.dispatch(new GetLayerVisibility());

        this.areGeofencesEnabled =
            configService.featureFlags.areGeofencesEnabled;
        this.arePublicWaypointsEnabled = configService.isOffRoad;
        this.areRideAreasEnabled =
            configService.featureFlags.areRideAreasEnabled;
        this.areSponsorsEnabled = configService.featureFlags.areSponsorsEnabled;
    }

    ngOnInit(): void {
        this.checkBrowserLocationPermission();
        this.vehicleType$ = this.rideTypeService.rideType$.pipe(
            map((type) => type as string)
        );

        this.activatedRoute.queryParamMap
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((params) => {
                const debugLayersParam = params.get('debugLayers');
                if (debugLayersParam === 'true') {
                    this.isDebuggingLayers = true;
                    this.userStoreService.setInSessionStorage(
                        'IS_DEBUGGING_LAYERS',
                        true
                    );
                    this.configService.isDebug = true;
                } else if (debugLayersParam === 'false') {
                    this.isDebuggingLayers = false;
                    this.userStoreService.setInSessionStorage(
                        'IS_DEBUGGING_LAYERS',
                        false
                    );
                } else {
                    this.isDebuggingLayers =
                        this.userStoreService.getFromSessionStorage<boolean>(
                            'IS_DEBUGGING_LAYERS'
                        ) ?? false;
                    if (this.isDebuggingLayers) {
                        this.configService.isDebug = true;
                    }
                }
                this.isDebug =
                    this.configService.isDebug || this.isDebuggingLayers;
            });

        if (this.enableMapTypeToggle === undefined) {
            this.enableMapTypeToggle =
                this.configService.featureFlags.isRideTypeToggleEnabled;
        }

        this.rideType$ = this.rideTypeService
            .rideType$ as Observable<VehicleType>;
        this.isSatellite$ = this.store.select(selectIsShowingSatellite);

        const initialVehicles$ = this.vehicles$ || of<Vehicle[]>([]);

        const vehiclesWithConnectedStatus$ = initialVehicles$.pipe(
            switchMap((vehicles) => {
                return forkJoin(
                    vehicles.map((vehicle) =>
                        forkJoin([
                            of(vehicle),
                            this.connectedVehicleService
                                .isConnectedVehicle(
                                    vehicle.vin,
                                    vehicle.modelId
                                )
                                .pipe(
                                    catchError((error) => {
                                        this.logService.error(
                                            this.className,
                                            error
                                        );
                                        return of(false);
                                    })
                                ),
                        ])
                    )
                );
            }),
            shareReplay(1)
        );

        const hasVehicleWithLocationEnabled$ =
            vehiclesWithConnectedStatus$.pipe(
                switchMap((vehicles) => {
                    return forkJoin(
                        vehicles
                            .filter(([, isConnected]) => isConnected)
                            .map(([vehicle]) =>
                                this.remoteOperationsService.getPrivacySettings(
                                    vehicle.vin
                                )
                            )
                    );
                }),
                map((privacySettings) =>
                    privacySettings.some(
                        (setting) => setting.allowLocationServices
                    )
                )
            );

        this.hasViewCurrentRidePermission$ = combineLatest([
            vehiclesWithConnectedStatus$,
            this.vehiclePermissionService.vinsWithCurrentRidePermission().pipe(
                catchError((error) => {
                    this.logService.error(this.className, error);
                    return of<string[]>([]);
                })
            ),
        ]).pipe(
            map(([connectedVehicles, ownedVehicles]) =>
                connectedVehicles.some(
                    (connectedVehicle: [Vehicle, boolean]) =>
                        connectedVehicle[1] &&
                        ownedVehicles.includes(connectedVehicle[0].vin)
                )
            )
        );

        this.hasLocationPermission$ = combineLatest([
            this.vehiclePermissionService.canViewLocationForAnyVin().pipe(
                catchError((error) => {
                    this.logService.error(this.className, error);
                    return of(false);
                })
            ),
            hasVehicleWithLocationEnabled$,
        ]).pipe(
            map(([ownedVehicles, hasVehicleWithLocationEnabled]) => {
                return ownedVehicles && hasVehicleWithLocationEnabled;
            })
        );
        defer(() => {
            this.getInitialMapParamsAndInitializeMap();
        });
        this.getInitialLegendState();
        this.doWhenRideTypeOrIsSatelliteChanges();

        this.activeRideGroup$ = this.store.pipe(
            select(selectActiveRideGroup),
            takeWhile(() => !this.isDestroyed)
        );
    }

    ngOnDestroy(): void {
        this.consoleLogService.debug('map component destroyed');
        this.destructors.forEach(attempt);
        this.isDestroyed = true;
    }

    addWaypoint(): void {
        this.createWaypoint.emit();
    }

    toggleSearch(): void {
        // this.matSidenav.toggle();
    }

    closeRideTypeSelect(): void {
        // User is closing map type select prompt without selecting a map type.
        // Save the current selection (should be ORV) which will cause the prompt to close.
        this.rideTypeService.setRideType('ORV');
    }

    zoomToGroup(): void {
        this.store.dispatch(new StopFollowingLeader());
        this.store.dispatch(new SetActiveRideGroupLeader(undefined));
        this.store.dispatch(new StartFollowingRideGroup());
        this.mapDisplay?.setZoomOverride(undefined);
    }

    vehicleLocate(): void {
        if (!this.vehiclesLocations$) {
            return;
        }

        combineLatest([
            this.mapDisplay?.mapSourcesLoaded$,
            this.vehiclesLocations$,
        ])
            .pipe(
                filter(
                    (vehicleLocations) =>
                        vehicleLocations && vehicleLocations.length > 0
                ),
                take(1)
            )
            .subscribe(([mapboxMap, vehicleLocations]) => {
                this.mapService.flyToCoordinate(
                    mapboxMap,
                    vehicleLocations[0].geoCoordinate,
                    10
                );
            });
    }

    checkBrowserLocationPermission(): void {
        navigator.permissions
            ?.query({ name: 'geolocation' })
            .then((permission) => {
                this.hasBrowserLocationPermission =
                    permission.state === 'granted';
            })
            .catch((error: unknown) => {
                this.logService.error(this.className, error);
            });
    }

    showBrowserLocationPrompt(): void {
        const chromeUrl =
            'https://support.google.com/chrome/answer/142065?co=GENIE.Platform%3DDesktop&hl=en&oco=0';
        const edgeUrl =
            'https://support.microsoft.com/en-us/microsoft-edge/location-and-privacy-in-microsoft-edge-31b5d154-0b1b-90ef-e389-7c7d4ffe7b04';
        const firefoxUrl =
            'https://support.mozilla.org/en-US/kb/does-firefox-share-my-location-websites?redirectslug=does-firefox-share-my-location-web-sites&redirectlocale=en-US';
        const operaUrl = 'https://help.opera.com/en/geolocation/';
        const safariUrl = 'https://support.apple.com/en-us/101331';

        let url = '';

        const browserType = this.utilService.getBrowserType();

        if (browserType === 'Chrome') {
            url = chromeUrl;
        } else if (browserType === 'Firefox') {
            url = firefoxUrl;
        } else if (browserType === 'Edge') {
            url = edgeUrl;
        } else if (browserType === 'Safari') {
            url = safariUrl;
        } else if (browserType === 'Opera') {
            url = operaUrl;
        }

        const data = new ConfirmDialogOptions({
            contentText: $localize`<div class="no-location-text"><p>First, try refreshing the page and clicking Current Location again. Make sure to click <strong>Allow</strong> or <strong>Grant Permissions</strong> if your browser asks for your location.</p><p>If you're still having trouble, check out <a href="${url}" target="_blank">location support page</a>. You can also search near a city, place, or address instead.</p></div>`,
            dialogId: 'noBrowserLocationPermission',
            hideCancelButton: true,
            showCloseButton: true,
            titleText: $localize`We Can't Find Your Location`,
        });
        this.matDialog.open(ConfirmDialogComponent, { data });
    }

    private initMap(
        isSatellite?: boolean,
        rideType?: string,
        center?: GeoCoordinate,
        zoom?: number,
        bounds?: GeoBounds
    ): MapboxMap {
        const isMapTypeSno = (rideType || '').toLowerCase() === 'sno';
        const mapBaseUri = this.mapService.getMapBaseUri(
            Boolean(isSatellite),
            isMapTypeSno
        );
        const mapOptions: MapOptions = {
            container: this.containerId,
            style: mapBaseUri,
        };

        if (this.isDebuggingLayers) {
            mapOptions.center = [-79.3, 56];
            mapOptions.zoom = 8;
        } else if (bounds) {
            mapOptions.bounds = new this.mapboxgl.mapbox.LngLatBounds(
                bounds.southWest,
                bounds.northEast
            );
        } else {
            if (zoom === undefined) {
                zoom = this.mapService.defaultZoom;
            }
            mapOptions.center = center || [0, 0];
            mapOptions.zoom = zoom;
        }

        const mapboxMap: MapboxMap = new this.mapboxgl.mapbox.Map(mapOptions);
        if (this.configService.environment !== 'prod' && this.window) {
            this.window.RIDE_COMMAND ??= {};
            const mapTestService = (this.window.RIDE_COMMAND.mapTest =
                new MapTestService(mapboxMap));
            this.destructors.push(() =>
                mapTestService.destroy.bind(mapTestService)
            );
        }
        this.consoleLogService.debug(`map initialized with ${mapBaseUri}`);

        if (!this.configService.featureFlags.is3dTerrainEnabled) {
            mapboxMap.dragRotate.disable();
            mapboxMap.touchZoomRotate.disableRotation();
        }

        if (this.disableDragPan) {
            mapboxMap.dragPan.disable();
            mapboxMap.boxZoom.disable();
            mapboxMap.doubleClickZoom.disable();
            mapboxMap.scrollZoom.disable();
            mapboxMap.keyboard.disable();
        }

        if (this.enableMapControls || this.enableZoomControl) {
            if (this.configService.featureFlags.is3dTerrainEnabled) {
                mapboxMap.addControl(
                    new this.mapboxgl.mapbox.NavigationControl({
                        showCompass: true,
                        showZoom: true,
                        visualizePitch: true,
                    })
                );
            } else {
                mapboxMap.addControl(
                    new this.mapboxgl.mapbox.NavigationControl()
                );
            }
        }

        this.mapDisplay = new MapDisplay(mapboxMap);
        this.destructors.push(() => {
            this.mapDisplay.destroy();
        });
        this.mapInitialized.emit(this.mapDisplay);

        this.addMapDisplayEvents();

        if (!this.isDebuggingLayers) {
            this.addSourcesAndLayersWhenMapStyleUpdates();
            this.addScaleControlWhenSystemOfMeasurementChanges(mapboxMap);
            this.addLayerVisibilityListeners();

            this.addMapLocationListeners();
            this.addMapDataListeners();
            this.addActiveStateListeners();
            this.addEditableStateListeners();
            this.resizeMapWhenMapContainerChanges(mapboxMap);
            this.doWhenRideTypeChanges();
        }

        return mapboxMap;
    }

    private setRadarWeather() {
        this.store
            .pipe(
                select(selectIsShowingRadar),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((isShowingRadar) => {
                if (isShowingRadar) {
                    if (
                        this.configService.browser.safariVersion &&
                        this.configService.browser.safariVersion < 10
                    ) {
                        this.weatherService
                            .updateRadarSource(this.mapDisplay.map)
                            .subscribe(() => {
                                this.weatherService.addRadarLayer(
                                    this.mapDisplay.map
                                );
                            });
                    } else {
                        this.weatherService.addRadarLayer(this.mapDisplay.map);
                    }
                    this.isRadarVisible = true;
                } else {
                    this.weatherService.removeRadarLayer(this.mapDisplay.map);
                    this.isRadarVisible = false;
                }
            });
    }

    private setCloudCover() {
        this.store
            .pipe(
                select(selectIsShowingCloudCover),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((isShowingCloudCover) => {
                if (isShowingCloudCover) {
                    if (
                        this.configService.browser.safariVersion &&
                        this.configService.browser.safariVersion < 10
                    ) {
                        this.weatherService
                            .updateCloudCoverSource(this.mapDisplay.map)
                            .subscribe(() => {
                                this.weatherService.addCloudCoverLayer(
                                    this.mapDisplay.map
                                );
                            });
                    } else {
                        this.weatherService.addCloudCoverLayer(
                            this.mapDisplay.map
                        );
                    }
                    this.isCloudCoverVisible = true;
                } else {
                    this.weatherService.removeCloudCoverLayer(
                        this.mapDisplay.map
                    );
                    this.isCloudCoverVisible = false;
                }
            });
    }

    private setSnowDepth() {
        combineLatest([
            this.store.select(selectIsShowingSnowDepth),
            this.rideTypeService.isSnoRideType$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([isShowingSnowDepth, isRideTypeSnow]) => {
                if (isShowingSnowDepth && isRideTypeSnow) {
                    if (
                        this.configService.browser.safariVersion &&
                        this.configService.browser.safariVersion < 10
                    ) {
                        this.weatherService
                            .updateSnowDepthSource(this.mapDisplay.map)
                            .subscribe(() => {
                                this.weatherService.addSnowDepthLayer(
                                    this.mapDisplay.map
                                );
                            });
                    } else {
                        this.weatherService.addSnowDepthLayer(
                            this.mapDisplay.map
                        );
                    }
                    this.isSnowDepthVisible = true;
                } else {
                    this.weatherService.removeSnowDepthLayer(
                        this.mapDisplay.map
                    );
                    this.isSnowDepthVisible = false;
                }
            });
    }

    private addMapDisplayEvents(): void {
        this.mapDisplay.mapClick$.subscribe((mapEvent) => {
            this.clickHandler(mapEvent);
        });
        this.mapDisplay.mapExtents$.subscribe(() => {
            this.updateMap();
        });
        this.mapDisplay.mapMouseMove$.subscribe((mapEvent) => {
            this.onMouseMove(mapEvent);
        });
    }

    private addSources(mapboxMap: MapboxMap): void {
        this.dealerMapService.addSources(mapboxMap);
        this.adventureMapService.addSources(mapboxMap);
        this.inProgressRideMapService.addSources(mapboxMap);
        this.plannedRideMapService.addSources(mapboxMap);
        this.pointOfInterestMapService.addSources(mapboxMap);
        this.premiumPoiMapService.addSources(mapboxMap);
        this.rideAreaMapService.addSources(mapboxMap);
        this.rideGroupMapService.addSources(mapboxMap);
        this.trackedRideMapService.addSources(mapboxMap);
        this.trailMapService.addSources(mapboxMap);
        this.waypointMapService.addSources(mapboxMap);
        this.weatherService.addSources(mapboxMap);
        this.parcelsInformationService.addSources(mapboxMap);

        if (this.configService.featureFlags.arePublicWaypointsEnabled) {
            this.publicWaypointMapService.addSources(mapboxMap);
        }

        if (this.areGeofencesEnabled) {
            this.geofenceMapService.addSources(mapboxMap);
        }

        if (this.showLabelledPois) {
            this.pointOfInterestMapService.setLabelVisibility(mapboxMap, true);
        }

        if (this.enableCloudCover) {
            this.setCloudCover();
        }

        if (this.enableRadarWeather) {
            this.setRadarWeather();
        }

        if (this.enableRideCondition) {
            this.rideConditionMapService.addSources(mapboxMap);
        }

        if (this.enableSnowDepth) {
            this.setSnowDepth();
        }

        if (this.contactMessage$) {
            this.checkInContactMessageMapService.addSources(mapboxMap);
        }

        if (this.configService.isOffRoad) {
            this.heatmapService.addSources(mapboxMap);
        }

        if (this.configService.featureFlags.is3dTerrainEnabled) {
            this.terrainMapService.addSources(mapboxMap);
        }

        this.updateMap();

        // optimizations for hover
        this.createLayerIdToItemTypeMap();
        this.createSelectableLayersArray();

        this.mapDisplay.setMapSourcesLoaded();
        this.consoleLogService.debug('map sources added');
    }

    private createLayerIdToItemTypeMap(): void {
        const layerToTypeMap: Record<string, MapItemType> = {};

        this.adventureMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'adventure';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.dealerMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'dealer';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.geofenceMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'geofence';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.inProgressRideMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'inProgressRide';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.parcelsInformationService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                if (layerId.toLocaleLowerCase().includes('privateland')) {
                    mapboxMap[layerId] = 'PrivateLand';
                }

                if (layerId.toLocaleLowerCase().includes('mixedland')) {
                    mapboxMap[layerId] = 'MixedLand';
                }
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.plannedRideMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'plannedRide';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.pointOfInterestMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'poi';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.premiumPoiMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'premiumPoi';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.publicWaypointMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'publicWaypoint';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.rideAreaMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'rideArea';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.rideConditionMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'rideCondition';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.rideGroupMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'rideGroupRider';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.trackedRideMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'trackedRide';
                return mapboxMap;
            },
            layerToTypeMap
        );

        this.waypointMapService.layerIdsSelectable.reduce(
            (mapboxMap, layerId) => {
                mapboxMap[layerId] = 'waypoint';
                return mapboxMap;
            },
            layerToTypeMap
        );

        if (this.configService.featureFlags.areInteractiveTrailsEnabled) {
            this.trailMapService.layerIdsSelectable.reduce(
                (mapboxMap, layerId) => {
                    mapboxMap[layerId] = 'trail';
                    return mapboxMap;
                },
                layerToTypeMap
            );
        }

        this.layerIdToItemTypeMap = layerToTypeMap;
    }

    private createSelectableLayersArray(): void {
        this.layerIdsSelectable = [
            ...this.adventureMapService.layerIdsSelectable,
            ...this.dealerMapService.layerIdsSelectable,
            ...this.geofenceMapService.layerIdsSelectable,
            ...this.inProgressRideMapService.layerIdsSelectable,
            ...this.parcelsInformationService.layerIdsSelectable,
            ...this.plannedRideMapService.layerIdsSelectable,
            ...this.pointOfInterestMapService.layerIdsSelectable,
            ...this.premiumPoiMapService.layerIdsSelectable,
            ...this.publicWaypointMapService.layerIdsSelectable,
            ...this.rideAreaMapService.layerIdsSelectable,
            ...this.rideConditionMapService.layerIdsSelectable,
            ...this.rideGroupMapService.layerIdsSelectable,
            ...this.trackedRideMapService.layerIdsSelectable,
            ...this.waypointMapService.layerIdsSelectable,
            ...(this.configService.featureFlags.areInteractiveTrailsEnabled
                ? this.trailMapService.layerIdsSelectable
                : []),
        ];
    }

    private onMouseMove(mapEvent: MapMouseEvent): void {
        const mapboxMap = mapEvent.target;

        const features = this.mapService.getFeaturesAtPoint(
            mapboxMap,
            mapEvent.point,
            this.layerIdsSelectable
        );
        let featureId = '';
        let itemType: MapItemType = 'emptyArea';

        if (features.length) {
            const featureLayerId = features[0].layer?.id ?? '';
            featureId = features[0].properties?.id as string;
            itemType = this.layerIdToItemTypeMap[featureLayerId];
            // edge case, this should only be possible when the user has a dealer
            // selected and turns on the adventures toggle, use the adventure if they
            // hover on the same spot again as they are probably trying to hover the adventure
            if (itemType === 'dealer') {
                const adv = features.find(
                    (feat) => feat.properties?.dealerNumber === featureId
                );
                if (adv) {
                    featureId = adv.properties?.id as string;
                    itemType = this.layerIdToItemTypeMap[adv.layer?.id ?? ''];
                }
            }
        }

        this.mapDisplay?.triggerMapItemHover(itemType, featureId);

        if (this.isDebug) {
            const allFeaturesAtPoint = this.mapService.getFeaturesAtPoint(
                mapboxMap,
                mapEvent.point
            );
            this.layers = allFeaturesAtPoint.map((feature) => {
                return feature.layer?.id ?? '';
            });
            this.mouseLng = mapEvent.lngLat.lng;
            this.mouseLat = mapEvent.lngLat.lat;
        }
    }

    private clickHandler(mapEvent: MapMouseEvent): void {
        if (
            mapEvent.lngLat.lng < -180 ||
            mapEvent.lngLat.lng > 180 ||
            mapEvent.lngLat.lat < -90 ||
            mapEvent.lngLat.lat > 90
        ) {
            mapEvent.lngLat = mapEvent.lngLat.wrap();
        }

        if (this.isDebug) {
            this.consoleLogService.debug(
                `${mapEvent.lngLat.lng}, ${mapEvent.lngLat.lat}`
            );
        }

        if (this.isDebuggingLayers) {
            const features = this.mapService.getFeaturesAtPoint(
                mapEvent.target,
                mapEvent.point
            );
            features.forEach((feature) => {
                const layerId = feature.layer?.id || '';
                if (
                    !['industrial', 'water', 'wetlands'].includes(layerId) &&
                    !layerId.startsWith('aeroway') &&
                    !layerId.startsWith('County') &&
                    !layerId.startsWith('country') &&
                    !layerId.startsWith('hillshade') &&
                    !layerId.startsWith('landcover') &&
                    !layerId.startsWith('national') &&
                    !layerId.startsWith('place') &&
                    !layerId.startsWith('road')
                ) {
                    this.consoleLogService.debug(layerId, feature);
                }
            });
        } else {
            const features = this.mapService.getFeaturesAtPoint(
                mapEvent.target,
                mapEvent.point,
                this.layerIdsSelectable
            );
            let featureId = '';
            let itemType: MapItemType = 'emptyArea';

            if (features.length) {
                const feature = features[0];
                const featureLayerId = feature.layer?.id || '';
                featureId = feature.properties?.id as string;
                itemType = this.layerIdToItemTypeMap[featureLayerId];

                // if this is a ride group rider with a vin, it's our own vehicle
                if (
                    itemType === 'rideGroupRider' &&
                    Boolean(
                        feature.properties?.vehicleId || feature.properties?.vin
                    )
                ) {
                    itemType = 'vehicle';

                    if (feature.properties?.vehicleId) {
                        featureId = feature.properties.vehicleId as string;
                    }
                }
                // edge case, this should only be possible when the user has a dealer
                // selected and turns on the adventures toggle, use the adventure if they
                // click on the same spot again as they are probably trying to click the adventure
                if (itemType === 'dealer') {
                    const adv = features.find(
                        (feat) => feat.properties?.dealerNumber === featureId
                    );
                    if (adv?.layer) {
                        featureId = adv.properties?.id as string;
                        itemType = this.layerIdToItemTypeMap[adv.layer.id];
                    }
                }

                if (itemType === 'trail') {
                    featureId = String(feature.id ?? featureId);
                }
            }
            this.parcelsInformationService.saveParcelToCache(
                itemType,
                features
            );
            this.mapDisplay.triggerMapItemClick(itemType, featureId);
        }
    }

    private updateMapLayers(mapboxMap: MapboxMap): void {
        if (this.isCloudCoverVisible) {
            this.weatherService.addCloudCoverLayer(mapboxMap);
        }
        if (this.isRadarVisible) {
            this.weatherService.addRadarLayer(mapboxMap);
        }
        if (this.isSnowDepthVisible) {
            this.weatherService.addSnowDepthLayer(mapboxMap);
        }
        if (this.configService.featureFlags.is3dTerrainEnabled) {
            this.terrainMapService.set3dTerrain(mapboxMap);
        }
    }

    private updateMap(): void {
        const bounds = this.mapService.getBounds(this.mapDisplay.map);
        const zoom = this.mapService.getZoom(this.mapDisplay.map);

        if (
            this.configService.browser.safariVersion &&
            this.configService.browser.safariVersion < 10
        ) {
            if (this.isCloudCoverVisible) {
                this.weatherService.updateCloudCoverSource(this.mapDisplay.map);
            }
            if (this.isRadarVisible) {
                this.weatherService.updateRadarSource(this.mapDisplay.map);
            }
            if (this.isSnowDepthVisible) {
                this.weatherService.updateSnowDepthSource(this.mapDisplay.map);
            }
        }

        if (this.isDebug) {
            this.north = bounds.north;
            this.east = bounds.east;
            this.south = bounds.south;
            this.west = bounds.west;
            this.zoom = zoom;
        }
    }

    private changeRideType(
        rideType: VehicleType | undefined,
        isAuthenticated: boolean
    ): void {
        if (!rideType || !this.mapDisplay) {
            return;
        }

        if (rideType === 'ORV' || rideType === 'SNO') {
            this.rideAreaMapService.setRideTypeFilter(
                this.mapDisplay.map,
                rideType
            );
            if (isAuthenticated) {
                this.userProfileService
                    .setCurrentRideType$(rideType)
                    .subscribe();
            }
        }
    }

    private addSourcesAndLayersWhenMapStyleUpdates(): void {
        this.mapDisplay?.mapStyleChanged$
            .pipe(
                takeWhile(() => !this.isDestroyed),
                tap(() => {
                    this.consoleLogService.debug(
                        'map style changed, adding sources'
                    );
                })
            )
            .subscribe((mapboxMap) => {
                this.addSources(mapboxMap);
                this.updateMapLayers(mapboxMap);
            });
    }

    private addScaleControlWhenSystemOfMeasurementChanges(
        mapboxMap: MapboxMap
    ): void {
        if (this.hideMapScale) {
            return;
        }

        let scaleControl: ScaleControl;

        this.userProfileService
            .getIsMetric$()
            .pipe(
                distinctUntilChanged(),
                tap(() => {
                    this.consoleLogService.debug(
                        'culture changed, updating scale control'
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((isMetric) => {
                if (scaleControl) {
                    mapboxMap.removeControl(scaleControl);
                }

                scaleControl = new this.mapboxgl.mapbox.ScaleControl({
                    maxWidth: 100,
                    unit: isMetric ? 'metric' : 'imperial',
                });
                mapboxMap.addControl(scaleControl);
            });
    }

    //#region Layer visibility listeners
    private addLayerVisibilityListeners(): void {
        this.setDealerLayerOnVisibilityChange();
        this.setAdventuresLayerOnVisibilityChange();
        this.setAreasLayerOnVisibilityChange();
        this.setTrailFilterLayerOnVisibilityChange();
        this.setDesignatedUnpavedTrailsLayerOnVisibilityChange();
        this.setDesignatedPavedTrailsLayerOnVisibilityChange();
        this.setUnmaintainedTrailsLayerOnVisibilityChange();
        this.setTrailsAndDifficultyLayerOnVisibilityChange();
        this.setGeoLocateButtonVisibility();
        this.setPlannedRideLayerOnVisibilityChange();
        this.setPoiLayerOnVisibilityChange();
        this.setRideConditionLayerOnVisibilityChange();
        this.setRideAreaLayerOnVisibilityChange();
        this.setTrackedRideLayerOnVisibilityChange();
        this.setInProgressRideLayerOnVisibilityChange();
        this.setWaypointLayerOnVisibilityChange();
        this.setLandLayerOnVisibilityChange();
        this.filterDealersWithAdventures();
        this.setHeatmapLayerOnVisibilityChange();
    }

    private setLandLayerOnVisibilityChange(): void {
        const isShowingLand$ = this.store.select(selectIsShowingLand);

        combineLatest([isShowingLand$, this.mapDisplay.mapSourcesLoaded$])
            .pipe(
                tap(([isVisible]) => {
                    this.consoleLogService.debug(
                        `set land layer visibility: ${String(isVisible)}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isVisible, mapboxMap]) => {
                this.parcelsInformationService.setLayerVisibility(
                    mapboxMap,
                    this.enableLandInfo && isVisible
                );
            });
    }

    private setDealerLayerOnVisibilityChange(): void {
        const isShowingDealers$ = this.store.select(selectIsShowingDealers);
        combineLatest([isShowingDealers$, this.mapDisplay.mapSourcesLoaded$])
            .pipe(
                tap(([isVisible]) => {
                    this.consoleLogService.debug(
                        `set dealer layer visibility: ${String(isVisible)}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isVisible, mapboxMap]) => {
                this.dealerMapService.setLayerVisibility(mapboxMap, isVisible);
            });
    }

    private setAdventuresLayerOnVisibilityChange(): void {
        const isShowingAdventures$ = this.store.select(
            selectIsShowingAdventures
        );
        combineLatest([isShowingAdventures$, this.mapDisplay.mapSourcesLoaded$])
            .pipe(
                tap(([isVisible]) => {
                    this.consoleLogService.debug(
                        `set adventures layer visibility: ${String(isVisible)}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isVisible, mapboxMap]) => {
                this.adventureMapService.setLayerVisibility(
                    mapboxMap,
                    Boolean(isVisible)
                );
            });
    }

    // dealer/adventure combos are duplicated in the dealer layer, if both layers are
    // turned on we need to filter the duplicates out of the dealers layer on the map
    private filterDealersWithAdventures() {
        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.mapDisplay.mapExtents$,
            this.store.select(selectIsShowingDealers),
            this.store.select(selectIsShowingAdventures),
        ])
            .pipe(
                takeWhile(() => !this.isDestroyed),
                filter(([mapboxMap, extents]) =>
                    Boolean(mapboxMap && extents.bounds)
                )
            )
            .subscribe({
                next: ([mapboxMap, , showDealers, showAdventures]) => {
                    if (showDealers && showAdventures) {
                        this.dealerMapService.setHiddenDealers(
                            mapboxMap,
                            compact(
                                this.adventureMapService
                                    .getMapAdventures(mapboxMap)
                                    .filter((adv) => adv.dealerId)
                                    .map((adv) => adv.dealerId)
                            )
                        );
                    } else {
                        this.dealerMapService.setHiddenDealers(mapboxMap, []);
                    }
                },
            });
    }

    private setHeatmapLayerOnVisibilityChange() {
        combineLatest([
            this.store.select(selectIsShowingHeatmap),
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([isShowing, mapboxMap]) => {
                this.heatmapService.setVisibility(
                    mapboxMap,
                    Boolean(isShowing)
                );
            });
    }

    private setTrailFilterLayerOnVisibilityChange() {
        combineLatest([
            this.store.select(selectTrailFilter),
            this.mapDisplay.mapSourcesLoaded$,
        ]).subscribe(([trail, mapboxMap]) => {
            this.trailMapService.setLayerOpacityMatch(mapboxMap, trail || '');
        });
    }

    private setDesignatedUnpavedTrailsLayerOnVisibilityChange(): void {
        combineLatest([
            this.store.select(selectIsShowingDesignatedUnpavedTrails),
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(
                tap(([isVisible]) => {
                    this.consoleLogService.debug(
                        `set designated unpaved trails layer visibility: ${String(
                            isVisible
                        )}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isVisible, mapboxMap]) => {
                this.trailMapService.setDesignatedUnpavedLayerVisibility(
                    mapboxMap,
                    isVisible
                );
            });
    }

    private setDesignatedPavedTrailsLayerOnVisibilityChange(): void {
        combineLatest([
            this.store.select(selectIsShowingDesignatedPavedTrails),
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(
                tap(([isVisible]) => {
                    this.consoleLogService.debug(
                        `set designated paved trails layer visibility: ${String(
                            isVisible
                        )}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isVisible, mapboxMap]) => {
                this.trailMapService.setDesignatedPavedLayerVisibility(
                    mapboxMap,
                    isVisible
                );
            });
    }

    private setUnmaintainedTrailsLayerOnVisibilityChange(): void {
        combineLatest([
            this.store.select(selectIsShowingUnmaintainedTrails),
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(
                tap(([isVisible]) => {
                    this.consoleLogService.debug(
                        `set unmaintained trails layer visibility: ${String(
                            isVisible
                        )}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isVisible, mapboxMap]) => {
                this.trailMapService.setUnmaintainedLayerVisibility(
                    mapboxMap,
                    Boolean(isVisible)
                );
            });
    }

    private setTrailsAndDifficultyLayerOnVisibilityChange(): void {
        combineLatest([
            this.store.select(selectIsShowingTrails),
            this.rideTypeService.isSnoRideType$,
            this.store.select(selectIsShowingTrailDifficulty),
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(
                tap(([isVisible, isRideTypeSnow]) => {
                    this.consoleLogService.debug(
                        `set ${
                            isRideTypeSnow ? 'sno' : 'orv'
                        } trails layer visibility: ${String(isVisible)}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                ([
                    isVisible,
                    isRideTypeSnow,
                    isDifficultyVisible,
                    mapboxMap,
                ]) => {
                    if (isRideTypeSnow) {
                        this.trailMapService.setSnoLayerVisibility(
                            mapboxMap,
                            Boolean(isVisible)
                        );
                        this.trailMapService.setClosedLayerVisibility(
                            mapboxMap,
                            Boolean(isVisible)
                        );
                    } else {
                        this.trailMapService.setOrvLayerVisibility(
                            mapboxMap,
                            Boolean(isVisible)
                        );
                        this.trailMapService.setClosedLayerVisibility(
                            mapboxMap,
                            Boolean(isVisible)
                        );
                        this.trailMapService.setDifficultyLayerVisibility(
                            mapboxMap,
                            Boolean(isVisible && isDifficultyVisible)
                        );
                    }
                }
            );
    }

    private setAreasLayerOnVisibilityChange(): void {
        if (this.configService.featureFlags.areGeofencesEnabled) {
            const isShowingAreas$ = this.store.select(selectIsShowingGeofences);
            combineLatest([isShowingAreas$, this.mapDisplay.mapSourcesLoaded$])
                .pipe(
                    tap(([isVisible]) => {
                        this.consoleLogService.debug(
                            `set areas layer visibility: ${String(isVisible)}`
                        );
                    }),
                    takeWhile(() => !this.isDestroyed)
                )
                .subscribe(([isVisible, mapboxMap]) => {
                    this.geofenceMapService.setLayerVisibility(
                        mapboxMap,
                        isVisible
                    );
                });
        }
    }

    private setGeoLocateButtonVisibility(): void {
        if (this.enableMapControls) {
            const locateControl = new this.mapboxgl.mapbox.GeolocateControl();
            this.mapDisplay.map.addControl(locateControl, 'bottom-left');
        }
    }

    private setPlannedRideLayerOnVisibilityChange(): void {
        const isShowingPlannedRides$ = this.store.select(
            selectIsShowingPlannedRides
        );
        combineLatest([
            isShowingPlannedRides$,
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(
                tap(([isVisible]) => {
                    this.consoleLogService.debug(
                        `set planned ride layer visibility: ${String(
                            isVisible
                        )}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isVisible, mapboxMap]) => {
                this.plannedRideMapService.setLayerVisibility(
                    mapboxMap,
                    isVisible
                );
            });
    }

    private setPoiLayerOnVisibilityChange(): void {
        this.mapDisplay.mapSourcesLoaded$
            .pipe(
                tap(() => {
                    this.consoleLogService.debug(
                        `set poi layer visibility: true`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((mapboxMap) => {
                this.pointOfInterestMapService.setLayerVisibility(
                    mapboxMap,
                    true
                );
            });
    }

    private setRideConditionLayerOnVisibilityChange(): void {
        combineLatest([
            this.store.select(selectIsShowingRideConditions),
            this.rideTypeService.isSnoRideType$,
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([isVisible, isRideTypeSnow, mapboxMap]) => {
                this.rideConditionMapService.setLayerVisibility(
                    mapboxMap,
                    isVisible && isRideTypeSnow
                );
                this.isRideConditionVisible = isVisible && isRideTypeSnow;
            });
    }

    private setRideAreaLayerOnVisibilityChange(): void {
        if (!this.areRideAreasEnabled) {
            return;
        }

        const isShowingRideAreas = this.store.select(selectIsShowingRideAreas);
        combineLatest([isShowingRideAreas, this.mapDisplay.mapSourcesLoaded$])
            .pipe(
                tap(([isVisible]) => {
                    this.consoleLogService.debug(
                        `set ride area layer visibility: ${String(isVisible)}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isVisible, mapboxMap]) => {
                this.rideAreaMapService.setLayerVisibility(
                    mapboxMap,
                    isVisible
                );
            });
    }

    private setTrackedRideLayerOnVisibilityChange(): void {
        const isShowingTrackedRides = this.store.select(
            selectIsShowingTrackedRides
        );
        combineLatest([
            isShowingTrackedRides,
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(
                tap(([isVisible]) => {
                    this.consoleLogService.debug(
                        `set tracked ride layer visibility: ${String(
                            isVisible
                        )}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isVisible, mapboxMap]) => {
                this.trackedRideMapService.setLayerVisibility(
                    mapboxMap,
                    isVisible
                );
            });
    }

    private setInProgressRideLayerOnVisibilityChange(): void {
        const isShowingInProgressRide = this.store.select(
            selectIsShowingInProgressRide
        );
        combineLatest([
            isShowingInProgressRide,
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(
                tap(([isVisible]) => {
                    this.consoleLogService.debug(
                        `set tracked ride layer visibility: ${String(
                            isVisible
                        )}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isVisible, mapboxMap]) => {
                this.inProgressRideMapService.setLayerVisibility(
                    mapboxMap,
                    isVisible
                );
            });
    }

    private setWaypointLayerOnVisibilityChange(): void {
        const isShowingWaypoints = this.store.select(selectIsShowingWaypoints);
        combineLatest([isShowingWaypoints, this.mapDisplay.mapSourcesLoaded$])
            .pipe(
                tap(([isVisible]) => {
                    this.consoleLogService.debug(
                        `set waypoint layer visibility: ${String(isVisible)}`
                    );
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isVisible, mapboxMap]) => {
                this.waypointMapService.setLayerVisibility(
                    mapboxMap,
                    isVisible
                );
            });
    }
    //#endregion

    //#region Map location listeners
    private addMapLocationListeners(): void {
        const mapExtents$ = this.mapDisplay?.mapExtents$.pipe(
            filter((extents) => Boolean(extents))
        );
        this.panMapWhenBoundsInputChanges();
        this.getPoisWhenVisibilityToggledOrMapMoved(mapExtents$);
        this.alertUserWhenTurningOnPoisAndNotZoomedIn(mapExtents$);
        this.getPublicWaypointsWhenMapExtentsChanges(mapExtents$);
        this.getRideConditionsWhenMapExtentsChanges(mapExtents$);
    }

    private panMapWhenBoundsInputChanges(): void {
        if (!this.initialBounds) {
            return;
        }

        this.initialBounds
            .pipe(
                // skip first because map was initialized with this bounds
                skip(1),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((bounds) => {
                if (!bounds) {
                    return;
                }

                if (bounds?.zoomApproximate > this.maxInitialZoom + 2) {
                    this.mapService.easeToCoordinate(
                        this.mapDisplay.map,
                        bounds.centerGeoCoordinate,
                        this.maxInitialZoom
                    );
                } else {
                    this.mapService.panMapToBounds(this.mapDisplay.map, bounds);
                }
            });
    }

    private getPoisWhenVisibilityToggledOrMapMoved(
        mapExtents$: Observable<MapExtents>
    ): void {
        this.pointOfInterestMapService
            .updatePlacesWhenMapExtentsChanges(mapExtents$)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((places) => {
                this.store.dispatch(new SetPlacesInView(places));
            });
    }

    private alertUserWhenTurningOnPoisAndNotZoomedIn(
        mapExtents$: Observable<MapExtents>
    ): void {
        combineLatest([
            this.store.select(selectIsShowingFuel),
            this.store.select(selectIsShowingFood),
            this.store.select(selectIsShowingLodging),
            this.store.select(selectIsShowingUnknown),
        ])
            .pipe(
                filter(
                    ([
                        isShowingFuel,
                        isShowingFood,
                        isShowingLodging,
                        isShowingUnknown,
                    ]) =>
                        isShowingFuel ||
                        isShowingFood ||
                        isShowingLodging ||
                        isShowingUnknown
                ),
                withLatestFrom(mapExtents$),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([, mapExtents]) => {
                if (mapExtents.zoom <= 10) {
                    const message = $localize`Zoom in to view gas stations, restaurants, and other points of interest`;
                    this.matSnackBar.open(message, '', { duration: 2000 });
                }
            });
    }

    private getPublicWaypointsWhenMapExtentsChanges(
        mapExtents$: Observable<MapExtents>
    ): void {
        if (!this.arePublicWaypointsEnabled) {
            return;
        }
        this.publicWaypointMapService
            .getPublicWaypointsWhenMapExtentsChanges(mapExtents$)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe({
                error: (error) => {
                    this.logService.error(this.className, error);
                },
                next: (waypoints) => {
                    this.store.dispatch(
                        new SetPublicWaypointsInView(waypoints)
                    );
                },
            });
    }

    private getRideConditionsWhenMapExtentsChanges(
        mapExtents$: Observable<MapExtents>
    ): void {
        this.rideTypeService.rideType$
            .pipe(
                switchMap((rideType) => {
                    if (rideType === 'SNO') {
                        return this.rideConditionMapService.getRideConditionsWhenMapExtentsChanges(
                            mapExtents$
                        );
                    }

                    return NEVER;
                }),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((rideConditions) => {
                this.store.dispatch(
                    new UpdateRideConditionsInView(rideConditions)
                );
            });
    }

    //#endregion

    //#region Map data listeners
    private addMapDataListeners(): void {
        this.getDealersWhenMapLoaded();
        this.getPremiumPointsOfInterestWhenMapLoaded();
        this.getRideAreasWhenMapLoaded();
        this.setAdhocWaypointDataWhenAdhocWaypointChanged();
        this.setCheckInMessageWhenCheckInMessageChanges();
        this.setEditablePlannedRideWhenEditablePlannedRideChanges();
        this.setEditableSponsorWhenEditableSponsorChanges();
        this.setEditableWaypointDataWhenEditableWaypointChanges();
        this.setGeofenceDataWhenGeofencesChange();
        this.setInProgressRideDataWhenInProgressRideChanges();
        // this.setLocationAlertPathWhenAlertStatus();
        this.setMapBoundsWhenRequestedBoundsOrCenterChanges();
        this.setPlannedRidesDataWhenPlannedRidesChange();
        this.setPointsOfInterestDataWhenPointsOfInterestChange();
        this.setPublicWaypointDataWhenPublicWaypointsChange();
        this.setRideConditionDataWhenRideConditionsChange();
        this.setRideGroupDataWhenRideGroupRidersChange();
        this.setTempViapointDataWhenTempViapointChanges();
        this.setTrackedRideDataWhenTrackedRidesChange();
        this.setWaypointDataWhenWaypointsChange();
    }

    private getDealersWhenMapLoaded(): void {
        this.mapDisplay.mapSourcesLoaded$
            .pipe(
                switchMap(() =>
                    this.dealerService.getAllDealers$().pipe(
                        catchError((error) => {
                            this.logService.error(this.className, error);
                            return of<Dealer[]>([]);
                        })
                    )
                ),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(() => {
                // do nothing, we just need dealers loaded for hovering
            });
    }

    private getPremiumPointsOfInterestWhenMapLoaded(): void {
        if (!this.areSponsorsEnabled) {
            return;
        }

        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.store.select(selectPremiumPointsOfInterestInView),
            this.store.select(selectEditableSponsor),
        ])
            .pipe(
                withLatestFrom(this.store.select(selectActivePlannedRide)),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                ([
                    [mapboxMap, premiumPlaces, editableSponsor],
                    plannedRide,
                ]) => {
                    premiumPlaces = premiumPlaces.filter(
                        (place) =>
                            !this.isItemPartOfPlannedRide(
                                place.id,
                                plannedRide as PlannedRide
                            )
                    );

                    if (editableSponsor) {
                        premiumPlaces = premiumPlaces.filter(
                            (place) =>
                                place.factualId !== editableSponsor.factualId
                        );
                    }

                    this.premiumPoiMapService.setSourceDataFromModel(
                        mapboxMap,
                        premiumPlaces
                    );
                }
            );
    }

    private getRideAreasWhenMapLoaded(): void {
        if (!this.areRideAreasEnabled) {
            return;
        }

        this.mapDisplay.mapSourcesLoaded$
            .pipe(
                switchMap(() => this.rideAreaService.getAllRideAreas$()),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(() => {
                // do nothing, we just need ride areas loaded for hovering
            });
    }

    private setAdhocWaypointDataWhenAdhocWaypointChanged(): void {
        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.store.select(selectAdhocWaypoint),
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([mapboxMap, adhocWaypoint]) => {
                this.plannedRideMapService.setAdhocWaypoint(
                    mapboxMap,
                    adhocWaypoint?.isAdhocFromSearchResult
                        ? undefined
                        : adhocWaypoint
                );

                if (adhocWaypoint) {
                    this.pointOfInterestMapService.setSelectedFilter(
                        mapboxMap,
                        adhocWaypoint?.isAdhocFromSearchResult
                            ? adhocWaypoint.id
                            : undefined
                    );
                    this.premiumPoiMapService.setSelectedFilter(
                        mapboxMap,
                        undefined
                    );
                }

                if (!adhocWaypoint) {
                    this.pointOfInterestMapService.setSelectedFilter(
                        mapboxMap,
                        undefined
                    );
                }
            });
    }

    private setCheckInMessageWhenCheckInMessageChanges(): void {
        if (!this.contactMessage$) {
            return;
        }

        combineLatest([this.mapDisplay.mapSourcesLoaded$, this.contactMessage$])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([mapboxMap, contactMessage]) => {
                if (contactMessage) {
                    this.checkInContactMessageMapService.setSourceDataFromModel(
                        mapboxMap,
                        contactMessage
                    );
                }
            });
    }

    private setGeofenceDataWhenGeofencesChange(): void {
        if (!this.areGeofencesEnabled || !this.geofences$) {
            return;
        }

        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.geofences$,
            this.store.select(selectActiveGeofence),
            this.store.select(selectEditableGeofence),
            this.store.select(selectActiveCollection),
            this.store.select(selectActiveRideEvent),
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(
                ([
                    mapboxMap,
                    geofences,
                    activeGeofence,
                    editableGeofence,
                    collection,
                    rideEvent,
                ]) => {
                    const childGeofences = [
                        ...(collection?.allGeofences || []),
                        ...(rideEvent?.allGeofences || []),
                    ];
                    const forcedGeofences = compact([
                        ...childGeofences,
                        // force active geofence if not editing
                        editableGeofence ? undefined : activeGeofence,
                    ]);
                    const filteredGeofences = (geofences || []).filter(
                        (geofence) => {
                            return (
                                this.showOnMapService.getShouldShow(
                                    geofence.id
                                ) &&
                                // filter out forced geofences (de-dupe) and editable geofence
                                !some([...forcedGeofences, editableGeofence], {
                                    id: geofence.id,
                                })
                            );
                        }
                    );
                    const geofencesToShow = [
                        ...filteredGeofences,
                        ...forcedGeofences,
                    ];

                    this.geofenceMapService.setSourceDataFromModel(
                        mapboxMap,
                        geofencesToShow
                    );
                }
            );
    }

    private setEditablePlannedRideWhenEditablePlannedRideChanges(): void {
        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.store.select(selectEditablePlannedRide),
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([mapboxMap, editablePlannedRide]) => {
                this.plannedRideMapService.setEditSourceDataFromModel(
                    mapboxMap,
                    editablePlannedRide
                );
            });
    }

    private setEditableSponsorWhenEditableSponsorChanges(): void {
        if (!this.configService.featureFlags.areSponsorsEnabled) {
            return;
        }

        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.store.select(selectEditableSponsor),
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([mapboxMap, editableSponsor]) => {
                this.premiumPoiMapService.setTempSponsor(
                    mapboxMap,
                    editableSponsor
                );
            });
    }

    private setEditableWaypointDataWhenEditableWaypointChanges(): void {
        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.store.select(selectEditableWaypoint),
        ])
            .pipe(
                withLatestFrom(this.userAuthService.isAuthenticated$),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([[mapboxMap, editableWaypoint], isAuthenticated]) => {
                this.waypointMapService.setTempWaypoint(
                    mapboxMap,
                    editableWaypoint
                );

                if (editableWaypoint && !isAuthenticated) {
                    const lat = editableWaypoint.geoCoordinate.lat;
                    const lng = editableWaypoint.geoCoordinate.lng;
                    this.mapService.setMapCenterOverride(lat, lng);
                }
            });
    }

    private setInProgressRideDataWhenInProgressRideChanges(): void {
        if (!this.inProgressRides$) {
            return;
        }

        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.inProgressRides$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([mapboxMap, rides]) => {
                this.inProgressRideMapService.setSourceDataFromModel(
                    mapboxMap,
                    rides
                );
            });
    }

    private setMapBoundsWhenRequestedBoundsOrCenterChanges() {
        const mapMoveRequest$ = merge(
            this.mapDisplay.requestedBounds$,
            this.mapDisplay.requestedCenter$
        ).pipe(filter((boundsOrCenter) => Boolean(boundsOrCenter)));

        combineLatest([
            this.mapDisplay.mapSourcesLoaded$.pipe(
                filter((mapboxMap) => Boolean(mapboxMap))
            ),
            mapMoveRequest$,
            this.mapDisplay.zoomOverride$,
            this.store.select(selectFollowingState),
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([mapboxMap, boundsOrCenter, zoomOverride]) => {
                // don't pan if the user is in the middle of zooming in/out
                if (mapboxMap.isZooming()) {
                    return;
                }

                if (boundsOrCenter instanceof GeoCoordinate) {
                    const zoom = zoomOverride || mapboxMap.getZoom();
                    this.mapService.panMapToCenterOnGeoCoordinate(
                        mapboxMap,
                        boundsOrCenter,
                        zoom,
                        this.configService.pollTimers.rideGroupInfo * 1000 * 0.5
                    );
                } else if (zoomOverride !== undefined) {
                    this.mapService.panMapToCenterOnGeoCoordinate(
                        mapboxMap,
                        (boundsOrCenter as GeoBounds).centerGeoCoordinate,
                        zoomOverride,
                        this.configService.pollTimers.rideGroupInfo * 1000 * 0.5
                    );
                } else {
                    this.mapService.panMapToBounds(
                        mapboxMap,
                        (boundsOrCenter as GeoBounds).resizedBy(3),
                        this.maxInitialZoom,
                        this.configService.pollTimers.rideGroupInfo * 1000 * 0.5
                    );
                }
            });
    }

    private setPlannedRidesDataWhenPlannedRidesChange(): void {
        if (!this.plannedRides$) {
            return;
        }

        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.plannedRides$,
            this.store.select(selectActivePlannedRide),
            this.store.select(selectEditablePlannedRide),
            this.store.select(selectActiveCollection),
            this.store.select(selectActiveRideEvent),
            this.rideTypeService.rideType$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((combined) => {
                const [
                    mapboxMap,
                    plannedRides,
                    activePlannedRide,
                    editablePlannedRide,
                    collection,
                    rideEvent,
                    rideType,
                ] = combined as [
                    MapboxMap,
                    PlannedRide[],
                    PlannedRide,
                    PlannedRide,
                    Collection,
                    RideEvent,
                    VehicleType,
                ];
                const childPlannedRides = [
                    ...(collection?.allPlannedRides || []),
                    ...(rideEvent?.allPlannedRides || []),
                ];
                const forcedPlannedRides = compact([
                    ...childPlannedRides,
                    // force active planned ride if not editing
                    editablePlannedRide ? undefined : activePlannedRide,
                ]);
                const filteredPlannedRides = (plannedRides || []).filter(
                    (plannedRide) => {
                        return (
                            this.showOnMapService.getShouldShow(
                                plannedRide.id
                            ) &&
                            // filter out forced planned rides (de-dupe) and editable planned ride
                            !some(
                                [...forcedPlannedRides, editablePlannedRide],
                                { id: plannedRide.id }
                            ) &&
                            // filter out planned rides that don't match active ride type
                            (!this.configService.featureFlags
                                .isRideTypeToggleEnabled ||
                                (rideType === 'SNO' && plannedRide.isSno) ||
                                (rideType === 'ORV' && plannedRide.isOrv))
                        );
                    }
                );
                const plannedRidesToShow = [
                    ...filteredPlannedRides,
                    ...forcedPlannedRides,
                ];

                this.plannedRideMapService.setSourceDataFromModel(
                    mapboxMap,
                    plannedRidesToShow
                );
            });
    }

    private setPointsOfInterestDataWhenPointsOfInterestChange(): void {
        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.store.select(selectStandardPlacesInView),
            this.store.select(selectActivePointOfInterest),
            this.store.select(selectActivePremiumPoi),
            this.store.select(selectEditableSponsor),
            this.store.select(selectSearchResults),
        ])
            .pipe(
                withLatestFrom(
                    this.store.select(selectActivePlannedRide),
                    this.store.select(selectSearchIsActive)
                ),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                ([
                    [
                        mapboxMap,
                        places,
                        activePlace,
                        activePremiumPoi,
                        editableSponsor,
                        searchResults,
                    ],
                    plannedRide,
                    isSearching,
                ]) => {
                    places = places.filter(
                        (place) =>
                            !this.isItemPartOfPlannedRide(
                                place.id,
                                plannedRide as PlannedRide
                            )
                    );

                    if (isSearching) {
                        places = [
                            ...places,
                            ...searchResults
                                .filter((searchResult) => {
                                    return (
                                        searchResult.searchType === 'poi' &&
                                        !places.find(
                                            (x) =>
                                                x.foursquareId ===
                                                searchResult.foursquareId
                                        )
                                    );
                                })
                                .map((searchResult) =>
                                    SearchResult.toPoi(searchResult)
                                ),
                        ];
                    }

                    if (editableSponsor) {
                        places = places.filter(
                            (place) =>
                                place.factualId !== editableSponsor.factualId
                        );
                    }

                    if (activePlace && !some(places, { id: activePlace.id })) {
                        places = [...places, activePlace];
                    }

                    if (
                        activePremiumPoi &&
                        !some(places, { id: activePremiumPoi.id })
                    ) {
                        places = [...places, activePremiumPoi];
                    }

                    this.pointOfInterestMapService.setSourceDataFromModel(
                        mapboxMap,
                        places
                    );
                }
            );
    }

    private setPublicWaypointDataWhenPublicWaypointsChange(): void {
        if (!this.arePublicWaypointsEnabled) {
            return;
        }

        const activePlannedRide$ = this.store.select(selectActivePlannedRide);

        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.store.select(selectPublicWaypointsInView),
        ])
            .pipe(
                withLatestFrom(activePlannedRide$),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([[mapboxMap, waypoints], plannedRide]) => {
                let filteredWaypoints = waypoints || [];

                if (plannedRide) {
                    const plannedRideWaypointIds = plannedRide.waypoints.map(
                        (waypoint) => waypoint.id
                    );
                    filteredWaypoints = waypoints.filter(
                        (waypoint) =>
                            !includes(plannedRideWaypointIds, waypoint.id)
                    );
                }
                this.publicWaypointMapService.setSourceDataFromModel(
                    mapboxMap,
                    filteredWaypoints
                );
            });
    }

    private setRideConditionDataWhenRideConditionsChange(): void {
        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.store.select(selectRideConditionsInView),
        ])
            .pipe(
                withLatestFrom(
                    this.store.select(selectMaxRideConditionsAgeInHours)
                ),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([[mapboxMap, conditions], maxAge]) => {
                // we may have older ride conditions cached if max ride condition age was reduced
                const filteredConditions = (conditions || []).filter(
                    (rideCondition) => {
                        return rideCondition.isWithinMaxAge(maxAge);
                    }
                );
                this.rideConditionMapService.setSourceDataFromModel(
                    mapboxMap,
                    filteredConditions
                );
            });
    }

    private setRideGroupDataWhenRideGroupRidersChange(): void {
        combineLatest({
            activeAlerts: this.activeAlerts$ || of([] as ActiveVehicleAlert[]),
            deviceProfiles:
                this.deviceProfiles$ ||
                of([] as GroupRideDeviceProfileContract[]),
            inProgressRides:
                this.inProgressRides$ || of([] as readonly InProgressRideVm[]),
            locationAlertPaths:
                this.locationAlertPaths$ || of([] as LocationAlertPath[]),
            mapboxMap: this.mapDisplay?.mapSourcesLoaded$,
            rideGroupRiders: this.store.select(selectRideGroupRiders),
            vehicleLocations:
                this.vehiclesLocations$ || of([] as readonly VehicleLocation[]),
            vehicles: this.vehicles$ || of([] as Vehicle[]),
        })
            .pipe(
                withLatestFrom(
                    this.store.select(selectActiveRideGroupRider),
                    this.userProfileService.getUserProfile$()
                ),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                ([
                    {
                        activeAlerts,
                        deviceProfiles,
                        inProgressRides,
                        locationAlertPaths,
                        mapboxMap,
                        rideGroupRiders,
                        vehicleLocations,
                        vehicles,
                    },
                    leader,
                    userProfile,
                ]) => {
                    this.rideGroupMapService.setSourceDataFromModel(
                        activeAlerts,
                        deviceProfiles,
                        inProgressRides,
                        mapboxMap,
                        rideGroupRiders,
                        leader as RideGroupRiderStatus,
                        userProfile as UserProfile,
                        vehicleLocations,
                        vehicles,
                        locationAlertPaths
                    );
                }
            );
    }

    private setTempViapointDataWhenTempViapointChanges(): void {
        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.store.select(selectTempViapoint),
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([mapboxMap, plannedRideViapoint]) => {
                if (plannedRideViapoint) {
                    this.plannedRideMapService.setTempViapoint(
                        mapboxMap,
                        plannedRideViapoint
                    );
                } else {
                    this.plannedRideMapService.setTempViapoint(
                        mapboxMap,
                        undefined
                    );
                }
            });
    }

    private setTrackedRideDataWhenTrackedRidesChange(): void {
        if (!this.trackedRides$) {
            return;
        }

        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.trackedRides$,
            this.store.select(selectActiveTrackedRide),
            this.store.select(selectActiveCollection),
            this.store.select(selectActiveRideEvent),
            this.rideTypeService.rideType$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(
                ([
                    mapboxMap,
                    trackedRides,
                    activeTrackedRide,
                    collection,
                    rideEvent,
                    rideType,
                ]) => {
                    const childTrackedRides = [
                        ...(collection?.allTrackedRides || []),
                        ...(rideEvent?.allTrackedRides || []),
                    ];
                    const forcedTrackedRides = compact([
                        ...childTrackedRides,
                        activeTrackedRide,
                    ]);
                    const filteredTrackedRides = (trackedRides || []).filter(
                        (trackedRide) => {
                            return (
                                this.showOnMapService.getShouldShow(
                                    trackedRide.id
                                ) &&
                                // filter out forced tracked rides (de-dupe)
                                !some(forcedTrackedRides, {
                                    id: trackedRide.id,
                                }) &&
                                // filter out tracked rides that don't match the current rideType
                                (!this.configService.featureFlags
                                    .isRideTypeToggleEnabled ||
                                    (rideType === 'SNO' && trackedRide.isSno) ||
                                    (rideType === 'ORV' && trackedRide.isOrv))
                            );
                        }
                    );
                    const ridesToShow = [
                        ...filteredTrackedRides,
                        ...forcedTrackedRides,
                    ];

                    this.trackedRideMapService.setSourceDataFromModel(
                        mapboxMap,
                        ridesToShow
                    );
                }
            );
    }

    private setWaypointDataWhenWaypointsChange(): void {
        if (!this.waypoints$) {
            return;
        }

        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.waypoints$,
            this.store.select(selectEditableWaypoint),
            this.store.select(selectActiveWaypoint),
            this.store.select(selectActiveCollection),
            this.store.select(selectActiveRideEvent),
        ])
            .pipe(
                withLatestFrom(this.store.select(selectActivePlannedRide)),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                ([
                    [
                        mapboxMap,
                        waypoints,
                        editableWaypoint,
                        activeWaypoint,
                        collection,
                        rideEvent,
                    ],
                    plannedRide,
                ]) => {
                    const childWaypoints = [
                        ...(collection?.allWaypoints || []),
                        ...(rideEvent?.allWaypoints || []),
                    ];
                    const forcedWaypoints = compact([
                        ...childWaypoints,
                        // force active waypoint if not editing
                        editableWaypoint ? undefined : activeWaypoint,
                    ]);
                    const plannedRideWaypointIds =
                        plannedRide?.waypoints.map((waypoint) => waypoint.id) ||
                        [];
                    const filteredWaypoints = (waypoints || []).filter(
                        (waypoint) => {
                            return (
                                this.showOnMapService.getShouldShow(
                                    waypoint.id
                                ) &&
                                // filter out forced waypoints (de-dupe) and editable waypoint
                                !some([...forcedWaypoints, editableWaypoint], {
                                    id: waypoint.id,
                                }) &&
                                // filter out waypoints in active planned ride
                                !includes(plannedRideWaypointIds, waypoint.id)
                            );
                        }
                    );
                    const waypointsToShow = [
                        ...filteredWaypoints,
                        ...forcedWaypoints,
                    ];

                    this.waypointMapService.setSourceDataFromModel(
                        mapboxMap,
                        waypointsToShow
                    );
                }
            );
    }
    //#endregion

    //#region Active state listeners
    private addActiveStateListeners(): void {
        this.hidePermanentPopupWhenMapItemDeselected();
        this.selectDealerWhenActiveDealerChanges();
        this.selectAdventureWhenActiveAdventureChanges();
        this.selectAreasWhenActiveAreaChanges();
        this.selectInProgressRideWhenActiveInProgressRideChanges();
        this.selectItemsWhenActiveUserContentItemChanges();
        this.selectPointOfInterestWhenActivePointOfInterestChanges();
        this.selectPremiumPoiWhenActivePremiumPoiChanges();
        this.selectPublicWaypointWhenActivePublicWaypointChanges();
        this.selectRideAreaWhenActiveRideAreaChanges();
        this.selectRideConditionWhenActiveRideConditionChanges();
        this.selectRideGroupRiderWhenActiveRiderChanges();
        this.selectVehicleWhenActiveVehicleChanges();
        this.setRideTypeWhenMapItemSelected();
        this.zoomToActiveItemWhenActiveItemChanges();
        this.zoomToRideEventWhenActivePublicRideEventChanges();
        this.selectLandWhenActiveLandChanges();
        this.setCheckInMessageSelected();
    }

    private selectLandWhenActiveLandChanges(): void {
        combineLatest([
            this.store.select(selectActiveParcel),
            this.mapDisplay.mapSourcesLoaded$,
            this.store.select(selectIsRidePlanning),
            this.store.select(selectIsWaypointEditing),
            this.store.select(selectIsGeofenceEditing),
            this.store.select(selectIsTrackedRideEditing),
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(
                ([
                    parcel,
                    mapboxMap,
                    isRidePlanning,
                    isWaypointEditing,
                    isGeofenceEditing,
                    isTrackedRideEditing,
                ]: [Parcel, MapboxMap, boolean, boolean, boolean, boolean]) => {
                    const shouldDisableLandSelect =
                        isRidePlanning ||
                        isWaypointEditing ||
                        isGeofenceEditing ||
                        isTrackedRideEditing;

                    this.parcelsInformationService.setSelectedFilter(
                        mapboxMap,
                        shouldDisableLandSelect ? undefined : parcel?.id
                    );

                    if (parcel && !shouldDisableLandSelect) {
                        this.analyticsService.fireEvent(
                            'map click',
                            `land area|${parcel.description}`
                        );
                    }
                }
            );
    }

    private hidePermanentPopupWhenMapItemDeselected(): void {
        combineLatest([
            this.store.select(selectActiveDealer),
            this.store.select(selectActiveAdventure),
            this.store.select(selectActivePlannedRide),
            this.store.select(selectActivePointOfInterest),
            this.store.select(selectActivePremiumPoi),
            this.store.select(selectActivePublicWaypoint),
            this.store.select(selectActiveRideArea),
            this.store.select(selectActiveRideCondition),
            this.store.select(selectActiveTrackedRide),
            this.store.select(selectActiveWaypoint),
            this.store.select(selectAdhocWaypoint),
            this.store.select(selectIsRidePlanning),
            this.store.select(selectIsWaypointEditing),
            this.store.select(selectIsGeofenceEditing),
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(
                ([
                    dealer,
                    adventure,
                    plannedRide,
                    poi,
                    premiumPoi,
                    publicWaypoint,
                    rideArea,
                    rideCondition,
                    trackedRide,
                    waypoint,
                    adhocWaypoint,
                    isRidePlanning,
                    isWaypointEditing,
                    isGeofenceEditing,
                ]) => {
                    if (
                        isRidePlanning ||
                        isWaypointEditing ||
                        isGeofenceEditing ||
                        dealer ||
                        adventure ||
                        plannedRide ||
                        poi ||
                        premiumPoi ||
                        publicWaypoint ||
                        rideArea ||
                        rideCondition ||
                        trackedRide ||
                        waypoint ||
                        adhocWaypoint
                    ) {
                        return;
                    }

                    this.mapService.hidePermanentPopup();
                }
            );
    }

    private selectDealerWhenActiveDealerChanges(): void {
        const activeDealer$ = this.store.select(selectActiveDealer);
        combineLatest([activeDealer$, this.mapDisplay.mapSourcesLoaded$])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([dealer, mapboxMap]) => {
                this.dealerMapService.setSelectedFilter(mapboxMap, dealer?.id);

                if (dealer) {
                    this.store.dispatch(new SetIsShowingDealers(true));
                }
            });
    }

    private selectAdventureWhenActiveAdventureChanges(): void {
        const activeAdventure$ = this.store.select(selectActiveAdventure);
        combineLatest([activeAdventure$, this.mapDisplay.mapSourcesLoaded$])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([adventure, mapboxMap]) => {
                this.adventureMapService.setSelectedFilter(
                    mapboxMap,
                    parseInt(adventure?.id || '', 10)
                );

                if (adventure) {
                    this.store.dispatch(new SetIsShowingAdventures(true));
                }
            });
    }

    private selectAreasWhenActiveAreaChanges(): void {
        const activeAreas$ = this.store.select(selectActiveGeofence);
        combineLatest([activeAreas$, this.mapDisplay.mapSourcesLoaded$])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([areas, mapboxMap]) => {
                this.geofenceMapService.setSelectedFilter(mapboxMap, areas?.id);

                if (areas) {
                    this.store.dispatch(new SetIsShowingGeofences(true));
                }
            });
    }

    private setCheckInMessageSelected(): void {
        if (!this.contactMessage$) {
            return;
        }

        combineLatest([this.mapDisplay.mapSourcesLoaded$, this.contactMessage$])
            .pipe(
                takeWhile(() => !this.isDestroyed),
                filter(([, message]) => Boolean(message))
            )
            .subscribe(([mapboxMap, contactMessage]) => {
                this.checkInContactMessageMapService.setSelectedFilter(
                    mapboxMap,
                    contactMessage?.riderId
                );
            });
    }

    private selectInProgressRideWhenActiveInProgressRideChanges(): void {
        if (!this.activeInProgressRideId$) {
            return;
        }

        combineLatest([
            this.mapDisplay.mapSourcesLoaded$,
            this.activeInProgressRideId$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([mapboxMap, id]) => {
                this.inProgressRideMapService.setSelectedFilter(
                    mapboxMap,
                    id || ''
                );
            });
    }

    private selectItemsWhenActiveUserContentItemChanges(): void {
        combineLatest([
            this.store.select(selectActiveCollection),
            this.store.select(selectActiveGeofence),
            this.store.select(selectActivePlannedRide),
            this.store.select(selectActiveRideEvent),
            this.store.select(selectActiveTrackedRide),
            this.store.select(selectActiveWaypoint),
            this.store.select(selectAdhocWaypoint),
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(
                takeWhile(() => !this.isDestroyed),
                withLatestFrom(this.store.select(selectIsRidePlanning))
            )
            .subscribe(([combined, isRidePlanning]) => {
                const [
                    activeCollection,
                    activeGeofence,
                    activePlannedRide,
                    activeRideEvent,
                    activeTrackedRide,
                    activeWaypoint,
                    adhocWaypoint,
                    mapboxMap,
                ] = combined;
                let geofenceIds: string | string[];
                let plannedRideIds: string | string[];
                let trackedRideIds: string | string[];
                let waypointIds: string | string[];

                if (activeCollection) {
                    geofenceIds = activeCollection.allGeofences.map(
                        (geofence) => geofence.id
                    );
                    plannedRideIds = activeCollection.allPlannedRides.map(
                        (plannedRide) => plannedRide.id
                    );
                    trackedRideIds = activeCollection.allTrackedRides.map(
                        (trackedRide) => trackedRide.id
                    );
                    waypointIds = activeCollection.allWaypoints.map(
                        (waypoint) => waypoint.id
                    );
                } else if (activeRideEvent) {
                    geofenceIds = activeRideEvent.allGeofences.map(
                        (geofence) => geofence.id
                    );
                    plannedRideIds = activeRideEvent.allPlannedRides.map(
                        (plannedRide) => plannedRide.id
                    );
                    trackedRideIds = activeRideEvent.allTrackedRides.map(
                        (trackedRide) => trackedRide.id
                    );
                    waypointIds = activeRideEvent.allWaypoints.map(
                        (waypoint) => waypoint.id
                    );
                } else {
                    geofenceIds = activeGeofence?.id || [];
                    plannedRideIds = activePlannedRide?.id || [];
                    trackedRideIds = activeTrackedRide?.id || [];
                    waypointIds = activeWaypoint?.id || [];

                    if (activePlannedRide && !isRidePlanning) {
                        this.store.dispatch(new SetIsShowingPlannedRides(true));
                    }

                    if (activeTrackedRide) {
                        this.store.dispatch(new SetIsShowingTrackedRides(true));
                    }

                    if (activeWaypoint) {
                        this.store.dispatch(new SetIsShowingWaypoints(true));
                    }

                    if (adhocWaypoint?.id) {
                        plannedRideIds = plannedRideIds
                            ? [...plannedRideIds, adhocWaypoint.id]
                            : adhocWaypoint.id;
                    }
                }

                this.geofenceMapService.setMultiSelectFilter(
                    mapboxMap,
                    geofenceIds
                );
                this.plannedRideMapService.setMultiSelectFilter(
                    mapboxMap,
                    plannedRideIds
                );
                this.trackedRideMapService.setMultiSelectFilter(
                    mapboxMap,
                    trackedRideIds
                );
                this.waypointMapService.setMultiSelectFilter(
                    mapboxMap,
                    waypointIds
                );
            });
    }

    private selectPointOfInterestWhenActivePointOfInterestChanges(): void {
        combineLatest([
            this.store.select(selectActivePointOfInterest),
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(
                withLatestFrom(this.store.select(selectAdhocWaypoint)),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([[pointOfInterest, mapboxMap], adhocPoint]) => {
                if (!adhocPoint?.isAdhocFromSearchResult) {
                    this.pointOfInterestMapService.setSelectedFilter(
                        mapboxMap,
                        pointOfInterest?.id
                    );
                }

                // hack for going from selected premium poi directly to poi
                // remove when detail view no longer responsible for setting selected state
                if (pointOfInterest) {
                    this.premiumPoiMapService.setSelectedFilter(
                        mapboxMap,
                        undefined
                    );
                    this.plannedRideMapService.setAdhocWaypoint(
                        mapboxMap,
                        undefined
                    );
                }
            });
    }

    private selectPremiumPoiWhenActivePremiumPoiChanges(): void {
        combineLatest([
            this.store.select(selectActivePremiumPoi),
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([premiumPoi, mapboxMap]) => {
                this.premiumPoiMapService.setSelectedFilter(
                    mapboxMap,
                    premiumPoi?.id
                );

                // hack for going from selected poi directly to premium poi
                // remove when detail view no longer responsible for setting selected state
                if (premiumPoi) {
                    this.pointOfInterestMapService.setSelectedFilter(
                        mapboxMap,
                        undefined
                    );
                    this.plannedRideMapService.setAdhocWaypoint(
                        mapboxMap,
                        undefined
                    );
                }
            });
    }

    private selectPublicWaypointWhenActivePublicWaypointChanges(): void {
        if (!this.arePublicWaypointsEnabled) {
            return;
        }

        const activePublicWaypoint$ = this.store.select(
            selectActivePublicWaypoint
        );
        combineLatest([
            activePublicWaypoint$,
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([publicWaypoint, mapboxMap]) => {
                this.publicWaypointMapService.setSelectedFilter(
                    mapboxMap,
                    publicWaypoint?.id
                );

                if (publicWaypoint) {
                    this.store.dispatch(new SetIsShowingPublicWaypoints(true));
                }
            });
    }

    private selectRideAreaWhenActiveRideAreaChanges(): void {
        if (!this.areRideAreasEnabled) {
            return;
        }

        const activeRideArea$ = this.store.select(selectActiveRideArea);
        combineLatest([activeRideArea$, this.mapDisplay.mapSourcesLoaded$])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([rideArea, mapboxMap]) => {
                this.rideAreaMapService.setSelectedFilter(
                    mapboxMap,
                    rideArea?.id
                );

                if (rideArea) {
                    this.store.dispatch(new SetIsShowingRideAreas(true));
                }
            });
    }

    private selectRideGroupRiderWhenActiveRiderChanges(): void {
        combineLatest([
            this.store.select(selectActiveRideGroupRider).pipe(
                map((rider) => rider?.id),
                distinctUntilChanged()
            ),
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([riderId, mapboxMap]) => {
                this.rideGroupMapService.setSelectedFilter(mapboxMap, riderId);
            });
    }

    private selectRideConditionWhenActiveRideConditionChanges(): void {
        combineLatest([
            this.store.select(selectActiveRideCondition).pipe(
                map((condition) => condition?.id),
                distinctUntilChanged()
            ),
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([conditionId, mapboxMap]) => {
                this.rideConditionMapService.setSelectedFilter(
                    mapboxMap,
                    conditionId
                );
            });
    }

    private selectVehicleWhenActiveVehicleChanges(): void {
        if (!this.activeVehicleId$) {
            this.vehicleIsSelected = false;
            return;
        }

        combineLatest([
            this.activeVehicleId$,
            this.mapDisplay.mapSourcesLoaded$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([activeVehicleId, mapboxMap]) => {
                this.rideGroupMapService.setSelectedFilter(
                    mapboxMap,
                    activeVehicleId
                );
                this.vehicleIsSelected = activeVehicleId !== '';
            });
    }

    private setRideTypeWhenMapItemSelected() {
        merge(
            this.store.select(selectActiveDealer),
            this.store.select(selectActivePlannedRide),
            this.store.select(selectActivePublicWaypoint),
            this.store.select(selectActiveRideArea),
            this.store.select(selectActiveTrackedRide),
            this.store.select(selectActiveWaypoint)
        )
            .pipe(
                withLatestFrom(
                    this.rideTypeService.rideType$,
                    this.store.select(selectIsRidePlanning)
                ),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([activeItem, rideType, isRidePlanning]) => {
                if (
                    activeItem &&
                    this.configService.isOffRoad &&
                    !isRidePlanning
                ) {
                    if (!activeItem.isOrv && rideType === 'ORV') {
                        this.rideTypeService.setRideType('SNO');
                    } else if (!activeItem.isSno && rideType === 'SNO') {
                        this.rideTypeService.setRideType('ORV');
                    }
                }
            });
    }

    private zoomToActiveItemWhenActiveItemChanges() {
        if (this.initialMapCenter) {
            return;
        }

        const activeVehicleLocation$ = combineLatest([
            this.vehiclesLocations$ || of([] as readonly VehicleLocation[]),
            this.activeVehicleId$ || of(''),
        ]).pipe(
            map(([vehicleLocations, vehicleId]) => {
                return vehicleLocations.find(
                    (location) => location.vehicleId === vehicleId
                );
            }),
            startWith(null)
        );

        combineLatest([
            this.store.select(selectActiveCollection),
            this.store.select(selectActiveAdventure),
            this.store.select(selectActiveDealer),
            this.store.select(selectActiveGeofence),
            this.store.select(selectActivePointOfInterest),
            this.store.select(selectActivePremiumPoi),
            this.store.select(selectActivePublicWaypoint),
            this.store.select(selectActiveRideArea),
            this.store.select(selectActiveRideEvent),
            this.store.select(selectActiveTrackedRide),
            this.store.select(selectActiveWaypoint),
            this.store.select(selectAdhocWaypoint),
            this.store.select(selectActivePlannedRide),
            activeVehicleLocation$,
        ])
            .pipe(
                takeWhile(() => !this.isDestroyed),
                map((activeItems) => {
                    return compact(activeItems)[0];
                }),
                distinctUntilChanged(),
                withLatestFrom(
                    this.mapDisplay.mapSourcesLoaded$,
                    this.initialBounds?.pipe(startWith(null)) || of(null)
                ),
                filter(([, mapboxMap, bounds]) => !bounds || Boolean(mapboxMap))
            )
            .subscribe(([activeItem, mapboxMap]) => {
                if (!activeItem) {
                    return;
                }

                if (activeItem instanceof Collection && !activeItem.content) {
                    this.consoleLogService.warn(
                        'Active collection set before bounds calculated'
                    );
                    return;
                }

                this.zoomToSelectedItem(mapboxMap, activeItem);
            });
    }

    private zoomToRideEventWhenActivePublicRideEventChanges() {
        if (this.initialMapCenter) {
            return;
        }

        combineLatest([
            this.store.select(selectActiveRideEvent),
            this.mapDisplay.mapSourcesLoaded$,
            this.initialBounds?.pipe(startWith(null)) || of(null),
        ])
            .pipe(
                takeWhile(() => !this.isDestroyed),
                filter(([, , bounds]) => !bounds)
            )
            .subscribe(([rideEvent, mapboxMap]) => {
                if (!rideEvent?.bounds && !rideEvent?.geoCoordinate) {
                    return;
                } else if (!rideEvent?.bounds) {
                    this.zoomToSelectedItem(mapboxMap, rideEvent);
                    return;
                }

                this.mapService.easeToBounds(
                    mapboxMap,
                    rideEvent.bounds.resizedBy(1.2)
                );
                if (rideEvent.plannedRides?.length && this.plannedRides$) {
                    this.plannedRides$
                        .pipe(takeWhile(() => !this.isDestroyed))
                        .subscribe((plannedRides) => {
                            const allPlannedRides = [
                                ...plannedRides,
                                ...rideEvent.plannedRides,
                            ];
                            this.plannedRideMapService.setSourceDataFromModel(
                                mapboxMap,
                                allPlannedRides
                            );
                        });
                }

                if (rideEvent.geofences?.length && this.geofences$) {
                    this.geofences$
                        .pipe(takeWhile(() => !this.isDestroyed))
                        .subscribe((geofences) => {
                            const allGeofences = [
                                ...geofences,
                                ...rideEvent.geofences,
                            ];
                            this.geofenceMapService.setSourceDataFromModel(
                                mapboxMap,
                                allGeofences
                            );
                        });
                }

                if (rideEvent.trackedRides?.length && this.trackedRides$) {
                    this.trackedRides$
                        .pipe(takeWhile(() => !this.isDestroyed))
                        .subscribe((trackedRides) => {
                            const allTrackedRides = [
                                ...trackedRides,
                                ...rideEvent.trackedRides,
                            ];
                            this.trackedRideMapService.setSourceDataFromModel(
                                mapboxMap,
                                allTrackedRides
                            );
                        });
                }

                if (rideEvent.waypoints?.length && this.waypoints$) {
                    this.waypoints$
                        .pipe(takeWhile(() => !this.isDestroyed))
                        .subscribe((waypoints) => {
                            const combinedWaypoints = [
                                ...waypoints,
                                ...rideEvent.waypoints,
                            ];
                            this.waypointMapService.setSourceDataFromModel(
                                mapboxMap,
                                combinedWaypoints
                            );
                        });
                }
            });
    }
    //#endregion

    //#region Editable state listeners
    private addEditableStateListeners(): void {
        this.onPlannedRideEdit();
        this.onWaypointEdit();
    }

    private onPlannedRideEdit(): void {
        this.store
            .select(selectIsRidePlanning)
            .pipe(
                withLatestFrom(
                    this.store.select(selectActivePlannedRide),
                    this.mapDisplay.mapSourcesLoaded$
                ),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isRidePlanning, activePlannedRide, mapboxMap]) => {
                // hide popup when starting to edit a planned ride
                // show when done if planned ride still selected
                if (isRidePlanning) {
                    this.mapService.hidePermanentPopup();
                } else if (activePlannedRide) {
                    this.mapService.showPermanentPopup(
                        mapboxMap,
                        activePlannedRide
                    );
                }
            });
    }

    private onWaypointEdit(): void {
        this.store
            .select(selectIsWaypointEditing)
            .pipe(
                withLatestFrom(
                    this.store.select(selectActiveWaypoint),
                    this.mapDisplay.mapSourcesLoaded$
                ),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([isEditing, activeWaypoint, mapboxMap]) => {
                // hide popup when starting to edit waypoint
                // show when done if waypoint still selected
                if (isEditing) {
                    this.mapService.hidePermanentPopup();
                } else if (activeWaypoint) {
                    this.mapService.showPermanentPopup(
                        mapboxMap,
                        activeWaypoint
                    );
                }
            });
    }
    //#endregion

    private doWhenRideTypeChanges(): void {
        this.rideTypeService.rideType$
            .pipe(
                distinctUntilChanged(),
                // wait for map sources to load
                switchMap((rideType) =>
                    this.mapDisplay.mapSourcesLoaded$.pipe(map(() => rideType))
                ),
                withLatestFrom(this.userAuthService.isAuthenticated$),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([rideType, isAuthenticated]) => {
                this.consoleLogService.debug(
                    `rideType change in map component to %s rideType`,
                    rideType
                );

                this.changeRideType(rideType, isAuthenticated);
            });
    }

    //#region Map initialization
    private getInitialMapParamsAndInitializeMap(): void {
        const isSatelliteObservable =
            this.initializeInSatellite === undefined
                ? this.store.select(selectIsShowingSatellite)
                : of(this.initializeInSatellite);

        combineLatest([
            this.getInitialMapLocation(),
            isSatelliteObservable,
            this.getInitialRideType(),
        ])
            .pipe(take(1))
            .subscribe({
                error: () => {
                    // map display destroyed before map initialized, do nothing
                },
                next: ([initialLocation, isSatellite, rideType]) => {
                    this.initMap(
                        isSatellite,
                        rideType,
                        initialLocation.center,
                        initialLocation.zoom,
                        initialLocation.bounds
                    );
                },
            });
    }

    /**
     * @description - Determine map center and zoom to load with based on the following rules:
     * Use bounds passed from parent if any.
     * User location if "locateme" passed in URL.
     * Get from selected item if item selected.
     * Location override if stored in localStorage for this user.
     * Last location stored in localStorage for this user/machine.
     * Physical location if no cached mapCenter or mapCenterOverride.
     */
    private getInitialMapLocation(): Observable<{
        center?: GeoCoordinate;
        bounds?: GeoBounds;
        zoom?: number;
    }> {
        if (this.initialMapCenter) {
            return of({
                center: this.initialMapCenter,
                zoom: this.initialMapZoom,
            });
        } else if (this.initialBounds) {
            return this.initialBounds.pipe(
                exhaustMap((bounds) => {
                    if (!bounds) {
                        return this.getInitialMapLocationFromSelectedItems();
                    }

                    // zoom approximate is very rough, give it a buffer before overriding.
                    if (bounds.zoomApproximate > this.maxInitialZoom + 2) {
                        return of({
                            center: bounds.centerGeoCoordinate,
                            zoom: this.maxInitialZoom,
                        });
                    } else {
                        return of({ bounds });
                    }
                })
            );
        }

        return this.getInitialMapLocationFromSelectedItems();
    }

    private getInitialMapLocationFromSelectedItems(): Observable<{
        center?: GeoCoordinate;
        bounds?: GeoBounds;
        zoom?: number;
    }> {
        return this.activatedRoute.paramMap.pipe(
            switchMap((params) => {
                if (params.get('locateme')) {
                    return this.userLocationService.getUserLocation().pipe(
                        take(1),
                        map((geoCoordinate) => {
                            return {
                                center: geoCoordinate,
                                zoom: this.maxInitialZoom,
                            };
                        })
                    );
                }

                return this.getInitialActiveItem().pipe(
                    map((activeItem) => {
                        if (
                            (activeItem instanceof Collection ||
                                activeItem instanceof RideEvent ||
                                activeItem instanceof PlannedRide ||
                                activeItem instanceof TrackedRide) &&
                            activeItem.bounds
                        ) {
                            if (
                                !activeItem.bounds.centerGeoCoordinate
                                    ?.isNullLocation
                            ) {
                                return {
                                    bounds: activeItem.bounds.resizedBy(1.2),
                                };
                            }
                        }

                        let center: GeoCoordinate | undefined = (
                            activeItem as MapPoint
                        )?.geoCoordinate;
                        if (center && !center.isNullLocation) {
                            return {
                                center: (activeItem as MapPoint).geoCoordinate,
                                zoom: this.maxInitialZoom,
                            };
                        }

                        center = this.mapService.getMapCenterOverride();
                        const zoom = this.mapService.getInitialMapZoom();
                        if (center) {
                            this.mapService.removeMapCenterOverride();
                            return { center, zoom };
                        }

                        center = this.mapService.getInitialMapCenter();
                        if (center) {
                            return { center, zoom };
                        }

                        return undefined;
                    }),
                    switchMap((result) => {
                        if (result) {
                            return of(result);
                        }

                        // Zoom to user's location when allowed and found
                        this.userLocationService
                            .getUserLocation()
                            .pipe(
                                exhaustMap((geoCoordinate) =>
                                    this.mapDisplay.mapStyleChanged$.pipe(
                                        map((mapboxMap) => ({
                                            geoCoordinate,
                                            mapboxMap,
                                        }))
                                    )
                                ),
                                take(1)
                            )
                            .subscribe({
                                error: () => {
                                    // do nothing on error
                                },
                                next: ({ geoCoordinate, mapboxMap }) => {
                                    this.mapService.easeToCoordinate(
                                        mapboxMap,
                                        geoCoordinate,
                                        this.maxInitialZoom
                                    );
                                },
                            });

                        // But return default location to load in the meantime
                        return of({
                            center: new GeoCoordinate(0, 0),
                            zoom: 2.5,
                        });
                    })
                );
            })
        );
    }

    /**
     * @description Get observable of ride type map should be initialized with. Indian and Slingshot are always
     * initialized with their respective ride types, otherwise ride type is determined in order of: initialRideType
     * input, ridetype state parameter, ride type of selected item, local storage, current value in store.
     */
    private getInitialRideType(): Observable<RideType> {
        return this.getInitialActiveItem().pipe(
            withLatestFrom(this.activatedRoute.queryParamMap),
            switchMap(([activeItem, params]) => {
                let rideType: RideType | undefined;
                if (this.configService.isIndian) {
                    rideType = 'IND';
                } else if (this.configService.isSlingshot) {
                    rideType = 'SLG';
                } else if (this.initialRideType) {
                    rideType = this.initialRideType;
                } else if (params.get('ridetype')) {
                    rideType = params.get('ridetype') as RideType;
                } else if (
                    activeItem &&
                    (activeItem instanceof Dealer ||
                        activeItem instanceof PlannedRide ||
                        activeItem instanceof RideArea ||
                        activeItem instanceof TrackedRide ||
                        activeItem instanceof Waypoint ||
                        activeItem instanceof VehicleLocation)
                ) {
                    rideType = !activeItem.isOrv ? 'SNO' : 'ORV';
                }
                if (rideType) {
                    this.rideTypeService.setRideType(rideType);
                    return of(rideType);
                }

                return this.rideTypeService.rideType$.pipe(
                    take(1)
                ) as Observable<RideType>;
            })
        );
    }

    private getInitialActiveItem(): Observable<MapPoint | MapPolyline> {
        return forkJoin([
            this.store.select(selectActiveCollection).pipe(take(1)),
            this.store.select(selectActiveAdventure).pipe(take(1)),
            this.store.select(selectActiveDealer).pipe(take(1)),
            this.store.select(selectActiveGeofence).pipe(take(1)),
            this.store.select(selectActivePlannedRide).pipe(take(1)),
            this.store.select(selectActivePointOfInterest).pipe(take(1)),
            this.store.select(selectActivePremiumPoi).pipe(take(1)),
            this.store.select(selectActivePublicWaypoint).pipe(take(1)),
            this.store.select(selectActiveRideArea).pipe(take(1)),
            this.store.select(selectActiveRideEvent).pipe(take(1)),
            this.store.select(selectActiveTrackedRide).pipe(take(1)),
            this.store.select(selectActiveWaypoint).pipe(take(1)),
            this.store.select(selectActiveVehicleLocation).pipe(take(1)),
        ]).pipe(map((activeItems) => compact(activeItems)[0]));
    }

    private getInitialLegendState(): void {
        if (this.enableMapLegend === undefined) {
            this.enableMapLegend = this.configService.isOffRoad;
        }
        this.legendExpanded = Boolean(
            this.userStoreService.get<boolean>('isLegendOpen')
        );
    }
    //#endregion

    private resizeMapWhenMapContainerChanges(mapboxMap: MapboxMap): void {
        const mapResize = debounce(() => {
            if (mapboxMap.getContainer()?.clientWidth) {
                mapboxMap.resize();
            }
        }, 250);
        const observer = new ResizeObserver(mapResize);
        if (mapboxMap.getContainer()) {
            observer.observe(mapboxMap.getContainer());
        }
        this.destructors.push(() => {
            observer.disconnect();
        });
    }

    private doWhenRideTypeOrIsSatelliteChanges(): void {
        combineLatest([
            this.store.select(selectIsShowingSatellite),
            this.rideTypeService.rideType$,
        ])
            .pipe(
                pairwise(),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                ([
                    [isSatellitePrevious, rideTypePrevious],
                    [isSatellite, rideType],
                ]) => {
                    if (!this.mapDisplay) {
                        // map is still initializing, ignore
                        return;
                    }

                    const isMapTypeSnow =
                        (rideType || 'orv').toLowerCase() === 'sno';
                    const wasMapTypeSnow =
                        (rideTypePrevious || 'orv').toLowerCase() === 'sno';
                    if (this.isDebug) {
                        this.consoleLogService.debug(
                            `isSatellite or rideType changed. isSatellite: %s, rideType: %s`,
                            String(isSatellite),
                            rideType
                        );
                    }

                    if (
                        isSatellite !== isSatellitePrevious ||
                        isMapTypeSnow !== wasMapTypeSnow
                    ) {
                        // clear selectable layers while style resets
                        this.layerIdsSelectable = [];

                        this.mapService.changeMapBase(
                            this.mapDisplay.map,
                            isSatellite,
                            isMapTypeSnow
                        );
                    }
                }
            );
    }

    private zoomToSelectedItem(
        mapboxMap: MapboxMap,
        selectedItem:
            | MapPoint
            | MapPolyline
            | MapPolygon
            | Collection
            | VehicleLocation
    ): void {
        if (
            selectedItem instanceof Collection ||
            selectedItem instanceof PlannedRide ||
            (selectedItem instanceof RideEvent && selectedItem.bounds) ||
            selectedItem instanceof TrackedRide ||
            selectedItem instanceof Geofence
        ) {
            this.mapService.panMapToIncludeBounds(
                mapboxMap,
                selectedItem.bounds
            );
            return;
        }

        this.mapService.panMapToIncludeGeoCoordinate(
            mapboxMap,
            (selectedItem as MapPoint).geoCoordinate,
            15
        );
    }

    private isItemPartOfPlannedRide(
        itemId: string,
        activePlannedRide: PlannedRide
    ): boolean {
        if (
            !itemId ||
            !activePlannedRide ||
            !activePlannedRide.waypoints ||
            activePlannedRide.waypoints.length === 0
        ) {
            return false;
        }

        return (
            activePlannedRide.waypoints.filter(
                (waypoint) => waypoint.id === itemId
            ).length > 0
        );
    }
}
